import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { PAYMENT_MODES, CRUISE_COLOR_PALETTE, NAVY, TAC_RATE } from '../utils/constants';
import { usePricingLookup } from '../hooks/usePricing';
import { ratesFor, priceLoadFailedMessage, priceMissingMessage, PRICE_LOADING_MESSAGE } from '../utils/pricing';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { getBookingReference, generateBookingReference, getBookingReferences } from '../utils/bookingReference';
import { getWholeCruiseClosure, closureMessage } from '../utils/closures';
import { downloadTicket } from '../utils/ticketPDF';
import * as XLSX from 'xlsx';
import { FileText, Ticket, AlertTriangle, MessageCircle, IndianRupee } from 'lucide-react';
import RefundModal from './RefundModal';
import RecordPaymentModal from './RecordPaymentModal';
import InvoiceModal from './InvoiceModal';
import InvoicePrint from './InvoicePrint';
import SendTicketModal from './SendTicketModal';

// ─── Date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = (n) => { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
const formatDateWords = (d) => { if (!d) return ''; const [y,m,dd]=d.split('-').map(Number); return `${ordinal(dd)} ${MONTHS[m-1]} ${y}`; };
const toDisplayDate = (d) => { if (!d) return ''; const [y,m,dd]=d.split('-'); return `${dd}-${m}-${y}`; };
// Local-timezone today (toISOString is UTC — before 05:30 IST it returns yesterday)
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// ─── Pricing ──────────────────────────────────────────────────────────────────
// `pricing` throughout this file is the live lookup from usePricingLookup()
// ({ [cruise]: { [deck]: { adult, child } } }, or null while loading / after a
// failed read). Rates are always resolved through ratesFor(), which returns
// null for a missing or unusable slot — there is NO hardcoded fallback. Where
// a save would need a price it doesn't have (pax or deck change, new row), the
// save is refused with a visible message; FOC rows are exempt (total is 0).
// Fields whose edit needs the rate card to recompute the total.
const PRICE_FIELDS = new Set(['adults', 'children', 'deck']);
// Message for a refused save, picked from the table-level status and the slot.
const priceRefusalMessage = (pricing, pricingStatus, cruise, deck, what) => {
  if (pricingStatus === 'loading') return PRICE_LOADING_MESSAGE;
  if (pricingStatus === 'error' || !pricing) return priceLoadFailedMessage(cruise, deck, what);
  return priceMissingMessage(cruise, deck, what);
};

// ─── Column / payment constants ───────────────────────────────────────────────
const DECK_OPTIONS = ['Upper Deck', 'Middle Deck'];
const PAYMENT_STATUS_OPTIONS = ['', 'Confirmed', 'Pending', 'Balance', 'FOC', 'Cancelled'];
const PAYMENT_MODE_TO_KEY = {
  'Razorpay':'razorpay_amount', 'Cash':'cash_amount',
  'ICICI POS':'icici_amount', 'Bank Transfer':'bank_transfer_amount', 'GPay':'gpay_amount',
};
const PAYMENT_AMOUNT_KEYS = Object.values(PAYMENT_MODE_TO_KEY);

// Resolve a free-typed partner name to its partners-table id — exact
// (case/space-insensitive) and UNAMBIGUOUS matches only. A wrong link
// attributes money to the wrong partner, so anything uncertain stays null.
const resolvePartnerId = (partners, name) => {
  const t = (name || '').trim().toLowerCase();
  if (!t) return null;
  const matches = (partners || []).filter(p => (p.name || '').trim().toLowerCase() === t);
  return matches.length === 1 ? matches[0].id : null;
};

// ─── Undo helpers ─────────────────────────────────────────────────────────────
const FIELD_LABELS = {
  adults:'Adults', children:'Children', tour_leaders:'Tour Leaders',
  deck:'Deck', booking_name:'Booking Name', partner_name:'Partner',
  guest_phone:'Guest Phone', partner_phone:'Partner Phone',
  total_amount:'Total', tac:'TAC', payment_status:'Pay Status',
  razorpay_amount:'Razorpay', cash_amount:'Cash', icici_amount:'ICICI POS',
  bank_transfer_amount:'Bank Transfer', gpay_amount:'GPay',
  ticket_sent:'Ticket Sent', notes:'Notes',
  driver_commission:'Driver Comm.', checked:'Checked',
};
const fmtUndoVal = (key, val) => {
  if (val === null || val === undefined || val === '') return '(empty)';
  if (typeof val === 'boolean') return val ? 'checked' : 'unchecked';
  const col = TRACKER_COLS_LOOKUP[key];
  if (col?.fmt === 'currency') return fmtMoney(Number(val));
  return String(val);
};
// Filled after TRACKER_COLS is defined (below).
let TRACKER_COLS_LOOKUP = {};


// ─── Column definitions ───────────────────────────────────────────────────────
const TRACKER_COLS = [
  { key:'adults',               label:'Adults',       w:55,  src:'booking', input:'number' },
  { key:'children',             label:'Child',        w:55,  src:'booking', input:'number' },
  { key:'tour_leaders',         label:'TL',           w:55,  src:'booking', input:'number' },
  { key:'deck',                 label:'Deck',         w:110, src:'booking', input:'select', options:DECK_OPTIONS },
  { key:'booking_name',         label:'Booking Name', w:180, src:'booking', input:'text' },
  { key:'partner_name',         label:'Partner',      w:160, src:'booking', input:'text' },
  { key:'guest_phone',          label:'Guest Ph',     w:120, src:'booking', input:'text' },
  { key:'partner_phone',        label:'Partner Ph',   w:120, src:'booking', input:'text' },
  { key:'total_amount',         label:'Total',        w:90,  src:'booking', input:'number', fmt:'currency' },
  { key:'tac',                  label:'TAC',          w:90,  src:'booking', input:'number', fmt:'currency' },
  { key:'net_total',            label:'Net Total',    w:90,  src:'booking', computed:true,  fmt:'currency' },
  { key:'payment_status',       label:'Pay Status',   w:110, src:'booking', input:'select', options:PAYMENT_STATUS_OPTIONS },
  { key:'razorpay_amount',      label:'Razorpay',     w:90,  src:'booking', input:'number', fmt:'currency', payment:true },
  { key:'cash_amount',          label:'Cash',         w:90,  src:'booking', input:'number', fmt:'currency', payment:true },
  { key:'icici_amount',         label:'ICICI POS',    w:90,  src:'booking', input:'number', fmt:'currency', payment:true },
  { key:'bank_transfer_amount', label:'Bank Transf.', w:90,  src:'booking', input:'number', fmt:'currency', payment:true },
  { key:'gpay_amount',          label:'GPay',         w:90,  src:'booking', input:'number', fmt:'currency', payment:true },
  { key:'collect',              label:'Collect',      w:92,  src:'booking', input:'collect' },
  { key:'ticket_sent',          label:'Ticket',       w:130, src:'tracker', input:'ticket' },
  { key:'notes',                label:'Notes',        w:200, src:'tracker', input:'text' },
  { key:'driver_commission',    label:'Driver Comm.', w:90,  src:'tracker', input:'number' },
  { key:'checked',              label:'✓',            w:55,  src:'tracker', input:'checkbox' },
  { key:'booking_ref',          label:'Ref',          w:110, src:'booking', input:null },
  { key:'refund',               label:'Refund',       w:90,  src:'booking', input:'refund' },
  { key:'invoice',              label:'Invoice',      w:90,  src:'booking', input:'invoice' },
];
TRACKER_COLS_LOOKUP = Object.fromEntries(TRACKER_COLS.map(c => [c.key, c]));
const EDITABLE_COLS = TRACKER_COLS.filter(c => c.input && c.input !== 'refund' && c.input !== 'invoice' && c.input !== 'collect');
const EMPTY_TRACKER = { dbId:null, ticket_sent:false, notes:'', driver_commission:0, checked:false };
const FILTERABLE_KEYS = new Set(['deck','payment_status','partner_name']);

// ─── Row helpers ──────────────────────────────────────────────────────────────
const isExcluded = (status) => status === 'FOC' || status === 'Cancelled';

const getRowClass = (b) => {
  const s = b.payment_status;
  if (s === 'Cancelled') return 'tracker-row-cancelled';
  if (s === 'FOC') return 'tracker-row-foc';
  const paySum = PAYMENT_AMOUNT_KEYS.reduce((acc,k) => acc+(Number(b[k])||0), 0);
  const net = (Number(b.total_amount)||0) - (Number(b.tac)||0);
  if ((s==='Confirmed'||s==='Paid') && net>0 && Math.abs(paySum-net)<0.5) return 'tracker-row-confirmed';
  if (s==='Pending'||s==='Partial'||s==='Balance') return 'tracker-row-pending';
  if (!b.booking_name || !b.adults) return 'tracker-row-incomplete';
  return '';
};

const fmtMoney = (n) => n!=null && Number(n)!==0 ? `₹${Number(n).toLocaleString('en-IN')}` : '';

// ─── Shared cell helpers (module-level so SelectCell can use them) ────────────
const cs = (col) => ({ width: col.w, minWidth: col.w, maxWidth: col.w });
const dispStyle = (isSel) => ({
  display: 'block',
  padding: '3px 8px',
  outline: 'none',
  minHeight: 24,
  cursor: 'default',
  fontSize: '0.81rem',
  color: '#1a2a40',
  borderRadius: 3,
  border: isSel ? '2px solid #0A1628' : '2px solid transparent',
  background: isSel ? '#dde6f7' : 'transparent',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  userSelect: 'none',
  lineHeight: '18px',
  boxSizing: 'border-box',
  width: '100%',
});

// ─── SelectCell: custom dropdown with full keyboard control ───────────────────
// Uses a portal into document.body so the dropdown list escapes the table's
// overflow:auto clipping and always renders on top (z-index 9999).
// Uses a custom option list so Enter can open it cross-browser (Safari has no
// showPicker() for <select>).
function SelectCell({ col, b, ri, isSel, isEd,
                      onBookingChange, onBookingSave,
                      onFocusMe, onEnterEdit, onExitEdit, onNavKey }) {
  const val = b[col.key] || '';
  const [highlight, setHighlight] = useState(() => Math.max(0, col.options.indexOf(val)));
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);

  // When dropdown opens: sync highlight + measure trigger position for the portal
  useEffect(() => {
    if (!isEd) return;

    const idx = col.options.indexOf(val);
    setHighlight(idx >= 0 ? idx : 0);

    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const estimatedHeight = Math.min(col.options.length * 34 + 8, 240);
      const spaceBelow = window.innerHeight - r.bottom;
      const openUpward = spaceBelow < estimatedHeight && r.top > estimatedHeight;
      setDropPos({
        top: openUpward ? r.top - estimatedHeight : r.bottom,
        left: r.left,
        width: Math.max(r.width, 130),
      });
    }

    // Close on scroll or resize — the fixed position would be stale
    const close = () => onExitEdit();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isEd]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectOption = (opt) => {
    onBookingChange(b.id, col.key, opt);
    onBookingSave(b.id, col.key, opt, b); // b is the pre-change booking snapshot
    onExitEdit();
  };

  const handleKeyDown = (e) => {
    if (isEd) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, col.options.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
      if (e.key === 'Enter')     { e.preventDefault(); selectOption(col.options[highlight]); return; }
      if (e.key === 'Escape')    { e.preventDefault(); onExitEdit(); return; }
      onNavKey(e); // Tab / Left / Right fall through to cell navigation
    } else {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEnterEdit(); return; }
      onNavKey(e);
    }
  };

  // Portal: dropdown rendered in document.body, positioned with fixed coords
  const dropdownPortal = isEd ? createPortal(
    <div
      onMouseDown={(e) => e.preventDefault()} // prevent trigger div from blurring on click
      style={{
        position: 'fixed',
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 9999,
        background: '#ffffff',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 6,
        boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
        maxHeight: 240,
        overflowY: 'auto',
      }}
    >
      {col.options.map((opt, i) => (
        <div
          key={opt || '__empty'}
          onMouseEnter={() => setHighlight(i)}
          onClick={(e) => { e.stopPropagation(); selectOption(opt); }}
          style={{
            padding: '7px 11px',
            fontSize: '0.82rem',
            cursor: 'pointer',
            background: i === highlight ? '#0A1628' : 'transparent',
            color: i === highlight ? '#ffffff' : '#1a2a40',
            whiteSpace: 'nowrap',
          }}
        >
          {opt || '—'}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <td className="tracker-td tracker-td-editable" style={{ ...cs(col), padding: 0 }}>
      <div
        ref={triggerRef}
        tabIndex={0}
        data-row={ri}
        data-colkey={col.key}
        onFocus={onFocusMe}
        onClick={onEnterEdit}
        onBlur={() => { if (isEd) onExitEdit(); }}
        onKeyDown={handleKeyDown}
        style={{ ...dispStyle(isSel || isEd), position: 'relative' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 'calc(100% - 14px)' }}>
          {val || <span style={{ color: 'var(--color-ink-faint)' }}>—</span>}
        </span>
        <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: '0.55rem', opacity: 0.45 }}>▾</span>
      </div>
      {dropdownPortal}
    </td>
  );
}

// ─── PartnerCell: text input with autocomplete from partners list ─────────────
// Portal dropdown so it escapes the table's overflow:auto.
// Selecting a partner also fills partner_phone.
function PartnerCell({ col, b, ri, isSel, isEd,
                       onBookingChange, onBookingSave, onMultiSave,
                       onFocusMe, onEnterEdit, onExitEdit, onNavKey,
                       partners }) {
  const val = b[col.key] || '';
  const [inputVal, setInputVal] = useState(val);
  const [highlight, setHighlight] = useState(-1);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const tdRef = useRef(null);
  const inputRef = useRef(null);
  const skipBlurRef = useRef(false);
  const blurTimerRef = useRef(null);
  const inputValRef = useRef(inputVal); // tracks latest value, avoids stale closure in blur timer

  const filtered = inputVal.trim()
    ? partners.filter(p => p.name.toLowerCase().includes(inputVal.trim().toLowerCase()))
    : partners;

  useEffect(() => {
    return () => { if (blurTimerRef.current) clearTimeout(blurTimerRef.current); };
  }, []);

  useEffect(() => {
    if (!isEd) { setInputVal(b[col.key] || ''); inputValRef.current = b[col.key] || ''; return; }
    setInputVal(b[col.key] || '');
    inputValRef.current = b[col.key] || '';
    setHighlight(-1);
    if (tdRef.current) {
      const r = tdRef.current.getBoundingClientRect();
      const estimatedHeight = Math.min((partners.length || 1) * 40 + 8, 200);
      const spaceBelow = window.innerHeight - r.bottom;
      const openUpward = spaceBelow < estimatedHeight && r.top > estimatedHeight;
      setDropPos({ top: openUpward ? r.top - estimatedHeight : r.bottom, left: r.left, width: Math.max(r.width, 160) });
    }
    requestAnimationFrame(() => inputRef.current?.focus());
    // Don't close if the input still has focus — layout shifts from long text can
    // trigger a scroll event on the table container without actually losing focus.
    const close = () => { if (document.activeElement !== inputRef.current) onExitEdit(); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [isEd]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitText = (text) => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    skipBlurRef.current = true;
    // Keep partner_id in lockstep with the name: resolve free-typed text,
    // clear the link when the name is cleared or doesn't match uniquely.
    const fields = { partner_name: text || null, partner_id: resolvePartnerId(partners, text) };
    if (onMultiSave) {
      const prevFields = { partner_name: b.partner_name ?? null, partner_id: b.partner_id ?? null };
      Object.entries(fields).forEach(([key, v]) => onBookingChange(b.id, key, v));
      onMultiSave(b.id, fields, prevFields);
    } else {
      onBookingChange(b.id, col.key, text || null);
      onBookingSave(b.id, col.key, text || null, b); // b is pre-change snapshot
    }
    onExitEdit();
  };

  const selectPartner = (p) => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    skipBlurRef.current = true;
    const fields = { partner_name: p.name, partner_id: p.id ?? null };
    if (p.phone_number) fields.partner_phone = p.phone_number;
    if (p.preferred_payment_status) fields.payment_status = p.preferred_payment_status;
    // Capture prev values for undo (only fields we're changing)
    const prevFields = {};
    Object.keys(fields).forEach(k => { prevFields[k] = b[k] ?? null; });
    // Update local state for all fields
    Object.entries(fields).forEach(([key, v]) => onBookingChange(b.id, key, v));
    // Save all to Supabase in one call (prevFields for undo)
    if (onMultiSave) onMultiSave(b.id, fields, prevFields);
    else onBookingSave(b.id, 'partner_name', p.name, b);
    onExitEdit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onExitEdit(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      highlight >= 0 && filtered[highlight] ? selectPartner(filtered[highlight]) : commitText(inputVal);
      return;
    }
    if (e.key === 'Tab') {
      if (highlight >= 0 && filtered[highlight]) { e.preventDefault(); selectPartner(filtered[highlight]); return; }
      commitText(inputVal);
      return;
    }
    onNavKey(e);
  };

  const dropdownPortal = isEd && filtered.length > 0 ? createPortal(
    <div onMouseDown={e => e.preventDefault()}
      style={{ position:'fixed', top:dropPos.top, left:dropPos.left, width:dropPos.width,
               zIndex:9999, background:'#ffffff', border:'1px solid var(--color-border-strong)', borderRadius:6,
               boxShadow:'0 6px 20px rgba(0,0,0,0.22)', maxHeight:200, overflowY:'auto' }}>
      {filtered.map((p, i) => (
        <div key={p.id} onMouseEnter={() => setHighlight(i)} onClick={() => selectPartner(p)}
          style={{ padding:'6px 11px', fontSize:'0.8rem', cursor:'pointer',
                   background: i === highlight ? '#0A1628' : 'transparent',
                   color: i === highlight ? '#ffffff' : '#1a2a40' }}>
          <div style={{ fontWeight: 600 }}>{p.name}</div>
          {p.phone_number && <div style={{ fontSize:'0.7rem', opacity:0.7 }}>{p.phone_number}</div>}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <td ref={tdRef} className="tracker-td tracker-td-editable" style={{ ...cs(col), padding: 0 }}>
      {isEd ? (
        <>
          <input ref={inputRef} type="text" value={inputVal}
            onChange={e => { setInputVal(e.target.value); inputValRef.current = e.target.value; setHighlight(-1); }}
            onBlur={() => {
              blurTimerRef.current = setTimeout(() => {
                if (skipBlurRef.current) { skipBlurRef.current = false; return; }
                commitText(inputValRef.current); // use ref — avoids stale closure
              }, 300);
            }}
            onKeyDown={handleKeyDown}
            className="tracker-input"
            style={{ width:'100%', padding:'4px 6px' }}
          />
          {dropdownPortal}
        </>
      ) : (
        <div tabIndex={0} data-row={ri} data-colkey={col.key}
          onFocus={onFocusMe} onClick={onEnterEdit}
          onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); onEnterEdit(); return; } onNavKey(e); }}
          style={dispStyle(isSel)}>
          {val || <span style={{ color:'var(--color-ink-faint)' }}>—</span>}
        </div>
      )}
    </td>
  );
}

// ─── NewRowSelectCell: portal dropdown for select cols in inline new row ──────
function NewRowSelectCell({ col, nr, setNewRow, recalcNewRow, saveNewRow, onCancel }) {
  const val = nr[col.key] || '';
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(() => Math.max(0, col.options.indexOf(val)));
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const skipCloseRef = useRef(false);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const idx = col.options.indexOf(val);
    setHighlight(idx >= 0 ? idx : 0);
    const r = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = Math.min(col.options.length * 34 + 8, 240);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUpward = spaceBelow < estimatedHeight && r.top > estimatedHeight;
    setDropPos({ top: openUpward ? r.top - estimatedHeight : r.bottom, left: r.left, width: Math.max(r.width, 130) });
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectOption = (opt, fromTab = false) => {
    if (!fromTab) skipCloseRef.current = true;
    setNewRow(r => recalcNewRow({ ...r, [col.key]: opt }));
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, col.options.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
      if (e.key === 'Enter')     { e.preventDefault(); selectOption(col.options[highlight]); return; }
      if (e.key === 'Tab')       { selectOption(col.options[highlight], true); return; }
      if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); return; }
    } else {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); return; }
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
    }
  };

  const handleBlur = () => {
    if (skipCloseRef.current) { skipCloseRef.current = false; return; }
    setOpen(false);
  };

  const dropdown = open ? createPortal(
    <div onMouseDown={e => e.preventDefault()} style={{
      position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
      zIndex: 9999, background: '#ffffff', border: '1px solid var(--color-border-strong)', borderRadius: 6,
      boxShadow: '0 6px 20px rgba(0,0,0,0.22)', maxHeight: 240, overflowY: 'auto',
    }}>
      {col.options.map((opt, i) => (
        <div key={opt || '__empty'} onMouseEnter={() => setHighlight(i)} onClick={() => selectOption(opt)}
          style={{ padding: '7px 11px', fontSize: '0.82rem', cursor: 'pointer',
                   background: i === highlight ? '#0A1628' : 'transparent',
                   color: i === highlight ? '#ffffff' : '#1a2a40', whiteSpace: 'nowrap' }}>
          {opt || '—'}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <td className="tracker-td tracker-td-editable" style={{ ...cs(col), padding: 0 }}>
      <div ref={triggerRef} tabIndex={0} onClick={() => setOpen(o => !o)} onBlur={handleBlur} onKeyDown={handleKeyDown}
        style={{ display: 'block', padding: '4px 8px', outline: 'none', minHeight: 24, cursor: 'pointer',
                 fontSize: '0.81rem', color: val ? '#1a2a40' : 'var(--color-ink-faint)', background: 'transparent',
                 width: '100%', position: 'relative', lineHeight: '18px', whiteSpace: 'nowrap',
                 overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 'calc(100% - 14px)' }}>
          {val || '—'}
        </span>
        <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: '0.55rem', opacity: 0.45 }}>▾</span>
      </div>
      {dropdown}
    </td>
  );
}

// ─── NewRowPartnerInput: autocomplete for partner_name in inline new row ───────
// Syncs to newRow on every keystroke via onTyping so saveNewRow always has fresh data.
function NewRowPartnerInput({ col, value, partners, onTyping, onCommit, onSave, onCancel }) {
  const [inputVal, setInputVal] = useState(value || '');
  const [highlight, setHighlight] = useState(-1);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const [open, setOpen] = useState(false);
  const tdRef = useRef(null);
  const skipBlurRef = useRef(false);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    return () => { if (blurTimerRef.current) clearTimeout(blurTimerRef.current); };
  }, []);

  const filtered = inputVal.trim()
    ? partners.filter(p => p.name.toLowerCase().includes(inputVal.trim().toLowerCase()))
    : partners;

  useEffect(() => {
    if (!open || !tdRef.current) return;
    const r = tdRef.current.getBoundingClientRect();
    const estimatedHeight = Math.min((filtered.length || 1) * 40 + 8, 200);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUpward = spaceBelow < estimatedHeight && r.top > estimatedHeight;
    setDropPos({ top: openUpward ? r.top - estimatedHeight : r.bottom, left: r.left, width: Math.max(r.width, 160) });
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPartner = (p) => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    skipBlurRef.current = true;
    const fields = { partner_name: p.name, partner_id: p.id ?? null };
    if (p.phone_number) fields.partner_phone = p.phone_number;
    if (p.preferred_payment_status) fields.payment_status = p.preferred_payment_status;
    setInputVal(p.name);
    setHighlight(-1);
    setOpen(false);
    onCommit(fields);
  };

  const commitText = () => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    skipBlurRef.current = true;
    setOpen(false);
    onCommit({ partner_name: inputVal.trim() || null, partner_id: resolvePartnerId(partners, inputVal) });
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setInputVal(text);
    setHighlight(-1);
    setOpen(!!text);
    onTyping(text); // keep newRow.partner_name in sync for saveNewRow
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); onCancel(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); if (!open) setOpen(true); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      highlight >= 0 && filtered[highlight] ? selectPartner(filtered[highlight]) : commitText();
      return;
    }
    if (e.key === 'Tab') {
      if (highlight >= 0 && filtered[highlight]) selectPartner(filtered[highlight]);
      else commitText();
    }
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => {
      if (skipBlurRef.current) { skipBlurRef.current = false; return; }
      commitText();
    }, 180);
  };

  const dropdown = open && filtered.length > 0 ? createPortal(
    <div onMouseDown={e => e.preventDefault()} style={{
      position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
      zIndex: 9999, background: '#ffffff', border: '1px solid var(--color-border-strong)', borderRadius: 6,
      boxShadow: '0 6px 20px rgba(0,0,0,0.22)', maxHeight: 200, overflowY: 'auto',
    }}>
      {filtered.map((p, i) => (
        <div key={p.id} onMouseEnter={() => setHighlight(i)} onClick={() => selectPartner(p)}
          style={{ padding: '6px 11px', fontSize: '0.8rem', cursor: 'pointer',
                   background: i === highlight ? '#0A1628' : 'transparent',
                   color: i === highlight ? '#ffffff' : '#1a2a40' }}>
          <div style={{ fontWeight: 600 }}>{p.name}</div>
          {p.phone_number && <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>{p.phone_number}</div>}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <td ref={tdRef} className="tracker-td tracker-td-editable" style={{ ...cs(col), padding: 0 }}>
      <input type="text" value={inputVal}
        onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown}
        style={{ width: '100%', padding: '4px 6px', background: 'transparent', border: 'none',
                 outline: 'none', fontSize: '0.81rem', color: '#1a2a40', fontFamily: 'inherit' }}
      />
      {dropdown}
    </td>
  );
}

// ─── PaymentCell: selected/editing two-state with safe pre-fill ───────────────
// Keeps a local editVal draft so the pre-filled remaining-balance is NEVER
// written to the booking until the user explicitly commits (blur or Enter).
// escapeRef prevents onBlur from saving when the edit was discarded.
function PaymentCell({ col, b, ri, isSel, isEd, onFocusMe, onEnterEdit,
                       onCommit, onEscape, onNavKey }) {
  const rowNet     = (Number(b.total_amount)||0) - (Number(b.tac)||0);
  const paySum     = PAYMENT_AMOUNT_KEYS.reduce((s,k)=>s+(Number(b[k])||0), 0);
  const remaining  = rowNet - paySum;
  const isOverpaid = remaining < 0;
  const remStr     = remaining !== 0 ? String(Math.round(remaining)) : '';
  const savedVal   = Number(b[col.key]) || 0;

  const [editVal, setEditVal] = useState('');
  const escapeRef = useRef(false);

  // When entering edit mode: pre-fill with saved value (if any) else remaining balance
  useEffect(() => {
    if (!isEd) return;
    setEditVal(savedVal ? String(savedVal) : (remaining > 0 ? String(Math.round(remaining)) : ''));
    escapeRef.current = false;
  }, [isEd]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    const numVal = editVal === '' ? null : Number(editVal);
    onCommit(numVal);
  };

  const handleKeyDown = (e) => {
    if (isEd) {
      if (e.key === 'Escape') { e.preventDefault(); escapeRef.current = true; onEscape(); return; }
      if (e.key === 'Enter')  { e.preventDefault(); escapeRef.current = true; commit(); onEscape(); return; }
      // Tab: commit now, let browser move focus naturally; escapeRef suppresses the resulting blur
      if (e.key === 'Tab')    { escapeRef.current = true; commit(); }
    }
    onNavKey(e);
  };

  const handleBlur = () => {
    if (escapeRef.current) { escapeRef.current = false; return; }
    commit();
  };

  return (
    <td className="tracker-td tracker-td-editable" style={{...cs(col), padding:0}}>
      {isEd ? (
        <input type="number" min="0" autoFocus
          data-row={ri} data-colkey={col.key}
          value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`tracker-input${isOverpaid?' tracker-input-overdue':''}`}
          style={{width:'100%', padding:'4px 6px', textAlign:'right'}}
        />
      ) : (
        <div tabIndex={0} data-row={ri} data-colkey={col.key}
          onFocus={onFocusMe}
          onClick={onEnterEdit}
          onKeyDown={handleKeyDown}
          style={{...dispStyle(isSel), textAlign:'right'}}
        >
          {savedVal
            ? fmtMoney(savedVal)
            : remStr
              ? <span style={{color:isOverpaid?'#dc2626':'var(--color-ink-faint)',fontSize:'0.75rem'}}>{remStr}</span>
              : ''}
        </div>
      )}
    </td>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────
function exportCruiseTab(inputDate, cruiseType, bookings, trackerData) {
  const rows = bookings.map(b => {
    const t = trackerData[b.id] || EMPTY_TRACKER;
    const net = (Number(b.total_amount)||0)-(Number(b.tac)||0);
    return {
      Adults:b.adults||0, Child:b.children||0, TL:b.tour_leaders||0,
      Deck:b.deck||'', 'Booking Name':b.booking_name||'', Partner:b.partner_name||'',
      'Guest Ph':b.guest_phone||'', 'Partner Ph':b.partner_phone||'',
      Total:b.total_amount||0, TAC:b.tac||0, 'Net Total':net,
      Cruise:b.cruise_type||'', 'Pay Status':b.payment_status||'',
      Razorpay:b.razorpay_amount||0, Cash:b.cash_amount||0,
      'ICICI POS':b.icici_amount||0, 'Bank Transfer':b.bank_transfer_amount||0, GPay:b.gpay_amount||0,
      Ticket:t.ticket_sent?'Yes':'', Notes:t.notes||'',
      'Driver Comm.':t.driver_commission||0, Check:t.checked?'Yes':'',
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, cruiseType.replace(' Cruise',''));
  const [y,m,d] = inputDate.split('-');
  XLSX.writeFile(wb, `${d}-${m}-${y}_${cruiseType.replace(/ /g,'_')}.xlsx`);
}

// ─── Row-level recalc (mirrors handleBookingChange logic but operates on a booking object) ────
const applyBookingRecalc = (booking, field, value, pricing) => {
  const b = { ...booking, [field]: value };
  if (field === 'payment_status' && value === 'FOC') {
    b.total_amount = 0; b.tac = 0; b.net_total = 0;
    PAYMENT_AMOUNT_KEYS.forEach(k => { b[k] = 0; });
  } else if (field === 'adults' || field === 'children') {
    const a = field==='adults'   ? (Number(value)||0) : (Number(booking.adults)||0);
    const c = field==='children' ? (Number(value)||0) : (Number(booking.children)||0);
    const rates = ratesFor(pricing, booking.cruise_type, booking.deck);
    if (rates) { const tot=a*rates.adult+c*rates.child; const tac=(booking.partner_name?.trim())?Math.round(tot*TAC_RATE):0; b.total_amount=tot; b.tac=tac; b.net_total=tot-tac; }
  } else if (field === 'deck') {
    const a=Number(booking.adults)||0; const c=Number(booking.children)||0;
    const rates=ratesFor(pricing, booking.cruise_type, value);
    if (rates) { const tot=a*rates.adult+c*rates.child; const tac=(booking.partner_name?.trim())?Math.round(tot*TAC_RATE):0; b.total_amount=tot; b.tac=tac; b.net_total=tot-tac; }
  } else if (field === 'partner_name') {
    const tot=Number(booking.total_amount)||0; const tac=(value&&value.trim())?Math.round(tot*TAC_RATE):0;
    b.tac=tac; b.net_total=tot-tac;
  } else if (field === 'total_amount' || field === 'tac') {
    const tot=field==='total_amount'?(Number(value)||0):(Number(booking.total_amount)||0);
    const tac=field==='tac'         ?(Number(value)||0):(Number(booking.tac)||0);
    b.net_total=tot-tac;
  }
  return b;
};

// ─── CruiseTab ────────────────────────────────────────────────────────────────
function CruiseTab({ cruise, bookings, trackerData, onBookingChange, onBookingSave,
                     onTrackerChange, onTrackerSave, onExport, onAddBooking, onViewSeating, onViewReport, colors,
                     currentDate, partners, pricing, pricingStatus, onRetryPricing, onRefresh, onMultiSave,
                     onPushUndo, undoCount, onUndo, cruiseSetting, onDownloadTicket, onSendWhatsApp, onRowSave, onEditStateChange,
                     invoiceMap, onGenerateInvoice }) {
  const [refundTarget, setRefundTarget] = useState(null);
  // A refused row save (price unknown) — shown as a banner above the table and
  // cleared on the next edit action. The row stays in edit mode so nothing is lost.
  const [rowSaveError, setRowSaveError] = useState('');
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [search, setSearch]             = useState('');
  const [deckFilter, setDeckFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell]   = useState(null);
  const [editingRow, setEditingRow]     = useState(null);   // ri of row in manual-save edit mode
  const [editingRowDraft, setEditingRowDraft] = useState(null); // { booking, tracker }
  const [unsavedWarning, setUnsavedWarning]   = useState(null); // { targetRi } | null
  const [newRow, setNewRow]             = useState(null);
  const [newRowErrors, setNewRowErrors] = useState({});
  const [cellErrors, setCellErrors]     = useState({});
  const newRowRef = useRef(null);
  useEffect(() => { newRowRef.current = newRow; }, [newRow]);
  const editSnapshotRef       = useRef(null);
  const editingRowSnapshotRef = useRef(null); // { booking, tracker } at row-edit-start

  // Notify parent when row-edit state changes (for tab-switch guard)
  useEffect(() => { onEditStateChange?.(editingRow !== null); }, [editingRow]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSelected = (ri, key) => selectedCell?.ri===ri && selectedCell?.key===key;
  const isEditing  = (ri, key) => editingCell?.ri===ri  && editingCell?.key===key;

  const INIT_NEW_ROW = { adults:'', children:0, booking_name:'', deck:'', partner_name:'',
                         guest_phone:'', partner_phone:'', total_amount:'', tac:0, tacManual:false, payment_status:'Pending',
                         food_non_veg:0, food_veg:0, food_jain:0 };

  // Food preferences apply ONLY to the Dinner Cruise (this tab's cruise); must
  // total adults + children. Hidden + saved as 0 for Sunset/Sundown tabs.
  const isDinnerCruise = (cruise || '').toLowerCase().includes('dinner');

  // Recalculate totals for inline new row whenever key fields change.
  // Skips overwriting tac when tacManual:true (user typed it directly).
  const recalcNewRow = (updated) => {
    const a = Number(updated.adults) || 0;
    const c = Number(updated.children) || 0;
    const d = updated.deck || '';
    const rates = ratesFor(pricing, cruise, d);
    if (rates && (a || c)) {
      const total = a * rates.adult + c * rates.child;
      const tac = updated.tacManual ? updated.tac : (updated.partner_name?.trim() ? Math.round(total * TAC_RATE) : 0);
      return { ...updated, total_amount: total, tac };
    }
    // No rates: recalc tac from manual total_amount
    if (Number(updated.total_amount)) {
      const total = Number(updated.total_amount);
      const tac = updated.tacManual ? updated.tac : (updated.partner_name?.trim() ? Math.round(total * TAC_RATE) : 0);
      return { ...updated, tac };
    }
    return updated;
  };

  // Shift+N shortcut to add a new row
  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.key === 'N' && !editingCell && !newRow) {
        e.preventDefault();
        setNewRow(INIT_NEW_ROW);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingCell, newRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move selection to cell (arrow-key navigation — no edit mode)
  const focusCell = useCallback((ri, colKey) => {
    setEditingCell(null);
    setSelectedCell({ ri, key: colKey });
    requestAnimationFrame(() => {
      document.querySelector(`[data-row="${ri}"][data-colkey="${colKey}"]`)?.focus();
    });
  }, []);

  // ── Row-level edit helpers ──────────────────────────────────────────────────
  const enterRowEdit = (ri) => {
    if (editingRow === ri) return; // already editing this row
    if (editingRow !== null) { setUnsavedWarning({ targetRi: ri }); return false; }
    const b = displayedBookings[ri];
    if (!b) return false;
    const te = trackerData[b.id] || EMPTY_TRACKER;
    const snap = { booking: { ...b }, tracker: { ...te } };
    editingRowSnapshotRef.current = snap;
    setEditingRowDraft({ booking: { ...b }, tracker: { ...te } });
    setEditingRow(ri);
    setRowSaveError('');
    return true;
  };

  const cancelRowEdit = () => {
    setEditingRow(null);
    setEditingRowDraft(null);
    setEditingCell(null);
    setRowSaveError('');
    editingRowSnapshotRef.current = null;
  };

  const saveRowEdit = async () => {
    if (editingRow === null || !editingRowDraft || !editingRowSnapshotRef.current) return;
    const snap = editingRowSnapshotRef.current;
    const b = displayedBookings[editingRow] || snap.booking; // fall back to snapshot if row filtered
    const draftB = editingRowDraft.booking;
    const draftT = editingRowDraft.tracker;

    // Collect changed booking fields (non-payment — payment handled separately below)
    const nonPaymentBookingKeys = TRACKER_COLS.filter(c => c.src === 'booking' && c.input && !c.computed && !c.payment).map(c => c.key);
    const changedBooking = {};
    nonPaymentBookingKeys.forEach(k => { if (String(draftB[k]??'') !== String(snap.booking[k]??'')) changedBooking[k] = draftB[k]; });
    // Always include payment columns as Numbers — bypasses change-detection issues
    // caused by display-layer injection of net_total into payment fields for legacy bookings
    PAYMENT_AMOUNT_KEYS.forEach(k => { changedBooking[k] = Number(draftB[k]) || 0; });
    if (Object.keys(changedBooking).length > 0) {
      changedBooking.net_total = (Number(draftB.total_amount)||0) - (Number(draftB.tac)||0);
    }

    // Hard stop: a pax or deck change needs the live rate card to recompute the
    // total (applyBookingRecalc silently kept the old total when it had none).
    // Refuse rather than save a stale amount; the row stays in edit mode. FOC
    // rows are exempt — their total is 0 regardless of the rate card.
    const priceDependent = [...PRICE_FIELDS].some(k => k in changedBooking);
    const draftDeck = draftB.deck || snap.booking.deck;
    if (priceDependent && draftB.payment_status !== 'FOC' && !ratesFor(pricing, cruise, draftDeck)) {
      setRowSaveError(priceRefusalMessage(pricing, pricingStatus, cruise, draftDeck, 'The change'));
      return;
    }
    setRowSaveError('');

    // Collect changed tracker fields (checkbox/ticket save immediately — only these editable tracker fields here)
    const changedTracker = {};
    ['notes','driver_commission'].forEach(k => {
      if (String(draftT[k]??'') !== String(snap.tracker[k]??'')) changedTracker[k] = draftT[k];
    });

    setEditingRow(null);
    setEditingRowDraft(null);
    setEditingCell(null);
    editingRowSnapshotRef.current = null;

    if (Object.keys(changedBooking).length > 0 || Object.keys(changedTracker).length > 0) {
      await onRowSave(b.id, changedBooking, changedTracker, snap.booking, snap.tracker);
      onRefresh(); // re-fetch to sync local state after save
    }
  };

  // Cmd/Ctrl+S — save the currently editing row
  useEffect(() => {
    const handler = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 's') return;
      if (editingRow === null) return;
      e.preventDefault();
      saveRowEdit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingRow, editingRowDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enter edit mode on a cell (still used for select/partner/payment dropdowns)
  const enterEdit = (ri, colKey) => {
    enterRowEdit(ri);
    const b = displayedBookings[ri];
    if (b) {
      const te = trackerData[b.id] || EMPTY_TRACKER;
      const col = TRACKER_COLS.find(c => c.key === colKey);
      editSnapshotRef.current = col?.src === 'tracker' ? { ...te } : { ...b };
    } else {
      editSnapshotRef.current = null;
    }
    setEditingCell({ ri, key: colKey });
    setSelectedCell({ ri, key: colKey });
  };

  // Unified keydown handler — used by both display divs and editing inputs
  const handleKeyDown = (e, ri, col) => {
    const editing = editingCell?.ri===ri && editingCell?.key===col.key;
    const b = displayedBookings[ri];

    if (e.key === 'Escape') {
      e.preventDefault();
      if (editingRow === ri) { cancelRowEdit(); return; }
      setEditingCell(null);
      if (editing) {
        setSelectedCell({ ri, key: col.key });
        requestAnimationFrame(() => {
          document.querySelector(`[data-row="${ri}"][data-colkey="${col.key}"]`)?.focus();
        });
      } else {
        setSelectedCell(null);
      }
      return;
    }

    // Delete/Backspace on selected (non-editing) cell → clear value + save
    if ((e.key === 'Delete' || e.key === 'Backspace') && !editing && !col.computed && col.input && b) {
      e.preventDefault();
      const te = trackerData[b.id] || EMPTY_TRACKER;
      if (col.input === 'checkbox') {
        const prevV = col.src === 'tracker' ? te[col.key] : b[col.key];
        if (col.src === 'tracker') { onTrackerChange(b.id,col.key,false); onTrackerSave(b.id,col.key,false,prevV); }
        else { onBookingChange(b.id,col.key,false); onBookingSave(b.id,col.key,false,b); }
      } else if (col.input === 'select') {
        onBookingChange(b.id,col.key,''); onBookingSave(b.id,col.key,'',b);
      } else if (col.input === 'number' || col.payment) {
        if (col.src === 'tracker') { onTrackerChange(b.id,col.key,0); onTrackerSave(b.id,col.key,0,te[col.key]); }
        else { onBookingChange(b.id,col.key,0); onBookingSave(b.id,col.key,0,b); }
      } else {
        if (col.src === 'tracker') { onTrackerChange(b.id,col.key,''); onTrackerSave(b.id,col.key,'',te[col.key]); }
        else { onBookingChange(b.id,col.key,null); onBookingSave(b.id,col.key,null,b); }
      }
      return;
    }

    // Enter on selected (non-editing) non-checkbox, non-select cell → enter edit mode
    if (e.key === 'Enter' && !editing && col.input !== 'checkbox' && col.input !== 'select') {
      e.preventDefault();
      enterEdit(ri, col.key);
      return;
    }

    // Up/Down always navigates rows
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusCell(ri + (e.key === 'ArrowDown' ? 1 : -1), col.key);
      return;
    }

    // Left/Right navigates columns when not in text-edit mode
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const canNav = !editing || col.input === 'number' || col.input === 'checkbox' || col.input === 'select';
      if (canNav) {
        e.preventDefault();
        const idx  = EDITABLE_COLS.findIndex(c => c.key === col.key);
        const next = idx + (e.key === 'ArrowRight' ? 1 : -1);
        if (next >= 0 && next < EDITABLE_COLS.length) focusCell(ri, EDITABLE_COLS[next].key);
      }
    }
  };

  // Filter logic
  const filterDisplayed = (b) => {
    if (deckFilter && b.deck !== deckFilter) return false;
    if (statusFilter && b.payment_status !== statusFilter) return false;
    if (partnerFilter === 'with'    && !(b.partner_name||'').trim()) return false;
    if (partnerFilter === 'without' &&  (b.partner_name||'').trim()) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.booking_name||'').toLowerCase().includes(q)
          || (b.partner_name||'').toLowerCase().includes(q)
          || (b.guest_phone||'').toLowerCase().includes(q)
          || (b.partner_phone||'').toLowerCase().includes(q);
    }
    return true;
  };

  const displayedBookings = bookings.filter(filterDisplayed);
  const summaryBookings   = displayedBookings.filter(b => !isExcluded(b.payment_status));
  const hasFilter         = !!(search||deckFilter||statusFilter||partnerFilter);
  const clearFilters      = () => { setSearch(''); setDeckFilter(''); setStatusFilter(''); setPartnerFilter(''); };

  const hdrColor     = colors?.header  || NAVY;
  const bookingColor = colors?.booking || '#1a3a5c';
  const trackerColor = colors?.tracker || '#162040';

  const numSum = (key) => summaryBookings.reduce((s,b) => s+(Number(b[key])||0), 0);
  const tots = {
    adults: numSum('adults'), children: numSum('children'), tour_leaders: numSum('tour_leaders'),
    total_amount: numSum('total_amount'), tac: numSum('tac'),
    net_total: summaryBookings.reduce((s,b) => s+((Number(b.total_amount)||0)-(Number(b.tac)||0)), 0),
    // Net Collection (footer strip) is receipts-based: mode-column sums minus cash outflows,
    // the same definition as Reports / Home. Billed 'Collection' and 'TAC' stay billed-side.
    razorpay_amount: numSum('razorpay_amount'), cash_amount: numSum('cash_amount'),
    icici_amount: numSum('icici_amount'), bank_transfer_amount: numSum('bank_transfer_amount'),
    gpay_amount: numSum('gpay_amount'),
    driver_commission: summaryBookings.reduce((s,b)=>s+(Number((trackerData[b.id]||EMPTY_TRACKER).driver_commission)||0),0),
  };

  const decks = [...new Set(summaryBookings.map(b=>b.deck).filter(Boolean))].sort();
  const deckStats = decks.map(deck => {
    const db = summaryBookings.filter(b=>b.deck===deck);
    return { label:deck.replace(' Deck',''), adults:db.reduce((s,b)=>s+(Number(b.adults)||0),0), children:db.reduce((s,b)=>s+(Number(b.children)||0),0) };
  });

  // Food preference totals — Dinner Cruise only; summed over the same (non-excluded)
  // bookings as the other day totals. Sunset/Sundown carry no food data.
  const foodTotals = { nonVeg:numSum('food_non_veg'), veg:numSum('food_veg'), jain:numSum('food_jain') };
  const foodHasData = isDinnerCruise && (foodTotals.nonVeg + foodTotals.veg + foodTotals.jain) > 0;

  const getSummaryCell = (col) => {
    if (col.key === 'booking_name') return `${displayedBookings.length} booking${displayedBookings.length!==1?'s':''}`;
    if (tots[col.key] != null) {
      const v = tots[col.key];
      if (!v) return '';
      if (col.fmt === 'currency') return fmtMoney(v);
      return String(Math.round(v));
    }
    return '';
  };

  const fSel   = { background:'var(--color-sunken)', border:'1.5px solid var(--color-border-strong)', borderRadius:7, padding:'5px 10px', fontSize:'0.8rem', color:NAVY, outline:'none', cursor:'pointer' };
  const fSelOn = { ...fSel, background:`${hdrColor}18`, borderColor:hdrColor, fontWeight:600 };

  // ── Inline add-row helpers ──────────────────────────────────────────────────
  const saveNewRow = useCallback(async () => {
    const nr = newRowRef.current;
    if (!nr || !nr.adults || !nr.booking_name?.trim()) return;
    // Validate phones
    const gpDigits = (nr.guest_phone||'').replace(/\D/g,'');
    const ppDigits = (nr.partner_phone||'').replace(/\D/g,'');
    if (nr.guest_phone && gpDigits.length !== 10) { setNewRowErrors(p=>({...p,guest_phone:'Must be 10 digits'})); return; }
    if (nr.partner_phone && ppDigits.length !== 10) { setNewRowErrors(p=>({...p,partner_phone:'Must be 10 digits'})); return; }
    // Dinner Cruise: food preferences must total adults + children.
    const nrFoodRequired = (Number(nr.adults)||0) + (Number(nr.children)||0);
    const nrFoodTotal = (Number(nr.food_non_veg)||0) + (Number(nr.food_veg)||0) + (Number(nr.food_jain)||0);
    if (isDinnerCruise && nrFoodTotal !== nrFoodRequired) {
      setNewRowErrors(p=>({...p, food:`Food preferences must total ${nrFoodRequired} (adults + children).`})); return;
    }
    // Hard stop: no live price for this cruise + deck, no new row (FOC exempt).
    // recalcNewRow left total_amount at whatever was typed — never save that.
    const nrDeck = nr.deck || DECK_OPTIONS[0];
    if ((nr.payment_status || 'Pending') !== 'FOC' && !ratesFor(pricing, cruise, nrDeck)) {
      setNewRowErrors(p=>({...p, booking_name: priceRefusalMessage(pricing, pricingStatus, cruise, nrDeck, 'The booking')})); return;
    }
    // Block the Tracker quick-add when the whole cruise is closed online for this
    // date (deck-only closures don't block here).
    const closure = await getWholeCruiseClosure(cruise, toDisplayDate(currentDate));
    if (closure) { setNewRowErrors(p=>({...p, booking_name: closureMessage(closure, toDisplayDate(currentDate))})); return; }
    const { data: inserted, error } = await supabase.from('bookings').insert({
      date: toDisplayDate(currentDate),
      cruise_type: cruise,
      deck: nr.deck || DECK_OPTIONS[0],
      booking_name: nr.booking_name.trim(),
      adults: Number(nr.adults),
      children: Number(nr.children) || 0,
      tour_leaders: 0,
      food_non_veg: isDinnerCruise ? (Number(nr.food_non_veg) || 0) : 0,
      food_veg:     isDinnerCruise ? (Number(nr.food_veg) || 0) : 0,
      food_jain:    isDinnerCruise ? (Number(nr.food_jain) || 0) : 0,
      partner_name: nr.partner_name?.trim() || null,
      partner_id: resolvePartnerId(partners, nr.partner_name),
      guest_phone: nr.guest_phone?.trim() || null,
      partner_phone: nr.partner_phone?.trim() || null,
      total_amount: Number(nr.total_amount) || 0,
      tac: Number(nr.tac) || 0,
      net_total: (Number(nr.total_amount) || 0) - (Number(nr.tac) || 0),
      payment_status: nr.payment_status || 'Pending',
      razorpay_amount: Number(nr.razorpay_amount) || 0,
      cash_amount: Number(nr.cash_amount) || 0,
      icici_amount: Number(nr.icici_amount) || 0,
      bank_transfer_amount: Number(nr.bank_transfer_amount) || 0,
      gpay_amount: Number(nr.gpay_amount) || 0,
    }).select('id').single();
    if (!error && inserted) {
      setNewRow(null); setNewRowErrors({});
      if (onPushUndo) onPushUndo({ type: 'new_booking', id: inserted.id });
      if (cruiseSetting?.cruise_code) {
        generateBookingReference(inserted.id, cruiseSetting.cruise_code, toDisplayDate(currentDate)).catch(() => {});
      }
      if (onRefresh) onRefresh();
    }
  }, [currentDate, cruise, cruiseSetting, onRefresh, onPushUndo, pricing, pricingStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderNewRowCell = (col) => {
    const nr = newRow;
    // Net Total computed
    if (col.computed) {
      const net = (Number(nr.total_amount) || 0) - (Number(nr.tac) || 0);
      return <td key={col.key} className="tracker-td tracker-td-computed" style={cs(col)}>{net ? fmtMoney(net) : ''}</td>;
    }
    // Last column (booking_ref) — Save / Cancel buttons
    if (col.key === 'booking_ref') {
      const onCancel = () => { setNewRow(null); setNewRowErrors({}); };
      return (
        <td key={col.key} className="tracker-td" style={{ ...cs(col), padding: '3px 5px' }}>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <button
              onClick={saveNewRow}
              style={{ background: '#0A1628', color: '#C9A84C', border: 'none', borderRadius: 5,
                       padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Save
            </button>
            <button
              onClick={onCancel}
              style={{ background: 'transparent', color: 'var(--color-ink-muted)', border: '1px solid var(--color-border-strong)',
                       borderRadius: 5, padding: '3px 7px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        </td>
      );
    }

    // Skip tracker, checkbox/ticket, refund, invoice, collect cols, and other read-only cols
    if (!col.input || col.src === 'tracker' || col.input === 'checkbox' || col.input === 'ticket' || col.input === 'refund' || col.input === 'invoice' || col.input === 'collect') {
      return <td key={col.key} className="tracker-td" style={cs(col)} />;
    }

    const onCancel = () => { setNewRow(null); setNewRowErrors({}); };

    // Select cols — use portal dropdown with full keyboard support
    if (col.input === 'select') {
      return <NewRowSelectCell key={col.key} col={col} nr={nr}
        setNewRow={setNewRow} recalcNewRow={recalcNewRow}
        saveNewRow={saveNewRow} onCancel={onCancel} />;
    }

    // Partner name — autocomplete with portal dropdown
    if (col.key === 'partner_name') {
      return <NewRowPartnerInput key={col.key} col={col}
        value={nr.partner_name || ''}
        partners={partners || []}
        onTyping={(text) => {
          // Sync partner_name to newRow AND newRowRef synchronously
          const updated = recalcNewRow({ ...newRowRef.current, partner_name: text || null });
          newRowRef.current = updated;
          setNewRow(updated);
        }}
        onCommit={(fields) => {
          const updated = recalcNewRow({ ...newRowRef.current, ...fields });
          newRowRef.current = updated;
          setNewRow(updated);
        }}
        onSave={saveNewRow}
        onCancel={onCancel}
      />;
    }

    const val = nr[col.key] ?? '';
    const isPhone = col.key === 'guest_phone' || col.key === 'partner_phone';
    const phoneErr = newRowErrors[col.key] || '';
    const sh = { width:'100%', padding:'4px 6px', background:'transparent', border:'none', outline:'none', fontSize:'0.81rem', color:'#1a2a40', fontFamily:'inherit' };

    // Validate phone only — does NOT auto-save on blur
    const onBlurValidate = (e) => {
      if (!isPhone) return;
      const digits = (e.target.value||'').replace(/\D/g,'');
      const err = e.target.value && digits.length !== 10 ? 'Must be 10 digits' : '';
      setNewRowErrors(prev => ({ ...prev, [col.key]: err }));
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
      if (e.key === 'Enter')  { e.preventDefault(); saveNewRow(); }
    };

    // Payment amount cols — placeholder shows remaining balance hint (display only, never auto-filled)
    if (col.payment) {
      const rowNet = (Number(nr.total_amount) || 0) - (Number(nr.tac) || 0);
      const paySum = PAYMENT_AMOUNT_KEYS.reduce((s, k) => s + (Number(nr[k]) || 0), 0);
      const remaining = rowNet - paySum;
      const isOverpaid = remaining < 0;
      const remStr = remaining !== 0 ? String(Math.round(remaining)) : '';
      const cur = Number(nr[col.key]) || 0;
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
          <input type="number" min="0" value={cur || ''}
            placeholder={remStr}
            onChange={e => setNewRow(r => ({ ...r, [col.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
            onBlur={onBlurValidate} onKeyDown={onKey}
            className={isOverpaid ? 'tracker-input tracker-input-overdue' : 'tracker-input'}
            style={{ ...sh, textAlign: 'right' }}
          />
        </td>
      );
    }

    // TAC — editable directly without auto-recalc override; sets tacManual flag
    if (col.key === 'tac') {
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
          <input type="number" min="0" value={val}
            onChange={e => setNewRow(r => ({ ...r, tac: e.target.value, tacManual: true }))}
            onBlur={onBlurValidate} onKeyDown={onKey}
            style={{ ...sh, textAlign: 'right' }} />
        </td>
      );
    }

    if (col.input === 'number') return (
      <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
        <input type="number" min="0" autoFocus={col.key==='adults'} value={val}
          onChange={e => setNewRow(r => recalcNewRow({...r, [col.key]: e.target.value}))}
          onBlur={onBlurValidate} onKeyDown={onKey}
          style={{...sh, textAlign:'right'}} />
      </td>
    );

    // Text input (includes phone fields)
    return (
      <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
        <div style={{display:'flex',flexDirection:'column'}}>
          <input type="text" value={val}
            onChange={e => setNewRow(r => ({...r, [col.key]: e.target.value}))}
            onBlur={onBlurValidate} onKeyDown={onKey} style={sh}
          />
          {phoneErr && <div style={{fontSize:'0.62rem',color:'#dc2626',padding:'0 6px'}}>{phoneErr}</div>}
        </div>
      </td>
    );
  };

  // ── Cell renderer ───────────────────────────────────────────────────────────
  const renderCell = (col, b, ri, te) => {
    const isSel    = isSelected(ri, col.key);
    const isRowEd  = editingRow === ri;
    const isCellEd = isEditing(ri, col.key); // dropdown/popup open for this specific cell

    // In row-edit mode: use draft values so edits are reflected before save
    const effectiveB  = isRowEd && editingRowDraft ? editingRowDraft.booking  : b;
    const effectiveTE = isRowEd && editingRowDraft ? editingRowDraft.tracker  : te;

    // Draft update helpers (only meaningful when isRowEd)
    const setDraftB = (field, value) =>
      setEditingRowDraft(prev => prev ? { ...prev, booking: applyBookingRecalc(prev.booking, field, value, pricing) } : prev);
    const setDraftT = (field, value) =>
      setEditingRowDraft(prev => prev ? { ...prev, tracker: { ...prev.tracker, [field]: value } } : prev);

    // Callbacks: in row-edit mode, update draft instead of saving immediately
    const effBChange  = isRowEd ? (id, field, value) => setDraftB(field, value)     : onBookingChange;
    const effTChange  = isRowEd ? (id, field, value) => setDraftT(field, value)     : onTrackerChange;
    const effBSave    = isRowEd ? () => {}                                           : onBookingSave;
    const effMulti    = isRowEd
      ? (id, fields) => setEditingRowDraft(prev => prev ? { ...prev, booking: { ...prev.booking, ...fields } } : prev)
      : onMultiSave;

    // Net Total: computed read-only (uses draft in row-edit mode)
    if (col.computed) return (
      <td key={col.key} className="tracker-td tracker-td-computed" style={cs(col)}>
        {fmtMoney((Number(effectiveB.total_amount)||0)-(Number(effectiveB.tac)||0))}
      </td>
    );

    // booking_ref column: show Save/Cancel when row is in edit mode; else show ref
    if (!col.input) {
      if (col.key === 'booking_ref' && isRowEd) {
        return (
          <td key={col.key} className="tracker-td" style={{ ...cs(col), padding: '3px 5px' }}>
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <button
                onClick={(e) => { e.stopPropagation(); saveRowEdit(); }}
                style={{ background:'#0A1628', color:'#C9A84C', border:'none', borderRadius:5,
                         padding:'3px 10px', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
              >
                Save
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); cancelRowEdit(); }}
                style={{ background:'transparent', color:'var(--color-ink-muted)', border:'1px solid var(--color-border-strong)',
                         borderRadius:5, padding:'3px 7px', fontSize:'0.72rem', fontWeight:600, cursor:'pointer' }}
              >
                ✕
              </button>
            </div>
          </td>
        );
      }
      return (
        <td key={col.key} className="tracker-td" style={cs(col)}>
          {b[col.key]??''}
        </td>
      );
    }

    // Collect button — opens RecordPaymentModal. Hidden for FOC/Cancelled;
    // disabled while the row is in edit mode (the row draft holds payment
    // values that would overwrite an RPC-recorded payment on save).
    if (col.input === 'collect') {
      if (isExcluded(b.payment_status)) {
        return <td key={col.key} className="tracker-td" style={cs(col)} />;
      }
      const paySum = PAYMENT_AMOUNT_KEYS.reduce((s, k) => s + (Number(b[k]) || 0), 0);
      const netAmt = (Number(b.total_amount) || 0) - (Number(b.tac) || 0);
      const settled = netAmt > 0 && Math.abs(paySum - netAmt) < 0.5;
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{ ...cs(col), textAlign: 'center', padding: '4px' }}>
          <button
            onClick={() => { if (!isRowEd) setPaymentTarget(b); }}
            disabled={isRowEd}
            title={isRowEd ? 'Save or cancel the row edit first' : 'Record a payment'}
            style={settled
              ? { fontSize: '0.65rem', color: '#166534', fontWeight: 700, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '3px 7px', cursor: isRowEd ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: isRowEd ? 0.5 : 1 }
              : { fontSize: '0.65rem', color: NAVY, fontWeight: 700, background: 'var(--color-sunken)', border: '1px solid var(--color-border-strong)', borderRadius: 6, padding: '3px 7px', cursor: isRowEd ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: isRowEd ? 0.5 : 1 }
            }
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <IndianRupee size={12} strokeWidth={1.75} aria-hidden="true" />{settled ? 'Paid' : 'Collect'}
            </span>
          </button>
        </td>
      );
    }

    // Refund button — opens RefundModal (Cancelled bookings only)
    if (col.input === 'refund') {
      if (b.payment_status !== 'Cancelled') {
        return <td key={col.key} className="tracker-td" style={cs(col)} />;
      }
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{ ...cs(col), textAlign: 'center', padding: '4px' }}>
          {b.is_refunded ? (
            <button
              onClick={() => setRefundTarget(b)}
              style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 700, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              ✓ Refunded
            </button>
          ) : (
            <button
              onClick={() => setRefundTarget(b)}
              style={{ fontSize: '0.65rem', color: '#dc2626', fontWeight: 700, background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Refund
            </button>
          )}
        </td>
      );
    }

    // Invoice button
    if (col.input === 'invoice') {
      const inv = invoiceMap?.get(b.id);
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{ ...cs(col), textAlign: 'center', padding: '4px' }}>
          <button
            onClick={() => onGenerateInvoice && onGenerateInvoice(b)}
            style={inv
              ? { fontSize: '0.65rem', color: '#1d4ed8', fontWeight: 700, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }
              : { fontSize: '0.65rem', color: NAVY,      fontWeight: 700, background: 'var(--color-sunken)', border: '1px solid var(--color-border-strong)', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }
            }
          >
            {inv ? 'Invoice ✓' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FileText size={13} strokeWidth={1.75} aria-hidden="true" />Invoice</span>}
          </button>
        </td>
      );
    }

    // Ticket button — always saves immediately (tracker field)
    if (col.input === 'ticket') {
      const sent = effectiveTE.ticket_sent ?? false;
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),textAlign:'center',padding:'4px'}}>
          <div style={{display:'inline-flex',alignItems:'center',gap:4,justifyContent:'center'}}>
            {sent ? (
              <span style={{fontSize:'0.68rem',color:'#15803d',fontWeight:700,letterSpacing:'0.2px'}}>✓ Sent</span>
            ) : (
              <button
                onClick={() => onDownloadTicket && onDownloadTicket(b)}
                style={{background:'#0A1628',color:'#C9A84C',border:'none',borderRadius:6,
                  padding:'3px 7px',fontSize:'0.68rem',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
                  display:'inline-flex',alignItems:'center',gap:4}}
              >
                <Ticket size={13} strokeWidth={1.75} aria-hidden="true" />Ticket
              </button>
            )}
            <button
              onClick={() => onSendWhatsApp && onSendWhatsApp(b)}
              title="Send via WhatsApp"
              aria-label="Send via WhatsApp"
              style={{background:'#25D366',color:'#fff',border:'none',borderRadius:6,
                padding:'3px 6px',fontSize:'0.68rem',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',
                display:'inline-flex',alignItems:'center',gap:4}}
            >
              <MessageCircle size={13} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </td>
      );
    }

    // Checkbox — always saves immediately (tracked directly)
    if (col.input === 'checkbox') {
      const isTrk = col.src === 'tracker';
      const val   = isTrk ? (effectiveTE[col.key] ?? false) : (effectiveB[col.key] ?? false);
      return (
        <td key={col.key} className={`tracker-td${isTrk?' tracker-td-editable':''}`} style={{...cs(col),textAlign:'center'}}>
          <input type="checkbox" checked={!!val}
            data-row={ri} data-colkey={col.key}
            onChange={(e) => {
              const v = e.target.checked;
              const prevV = !v;
              if (isTrk) { onTrackerChange(b.id,col.key,v); onTrackerSave(b.id,col.key,v,prevV); }
              else        { onBookingChange(b.id,col.key,v); onBookingSave(b.id,col.key,v,b); }
            }}
            onFocus={() => setSelectedCell({ri, key:col.key})}
            onKeyDown={(e) => handleKeyDown(e, ri, col)}
            style={{cursor:'pointer',width:15,height:15,accentColor:'#C9A84C'}}
          />
        </td>
      );
    }

    // Select — uses custom SelectCell; in row-edit mode uses draft values but still dropdown-on-click
    if (col.input === 'select') {
      if (col.key === 'deck' && cruiseSetting && !cruiseSetting.has_upper_deck) {
        return (
          <td key={col.key} className="tracker-td" style={{ ...cs(col), background:'#f3f4f6', color:'#6b7280', fontStyle:'italic', fontSize:'0.77rem', padding:'4px 8px', userSelect:'none' }}>
            Middle Deck
          </td>
        );
      }
      return (
        <SelectCell key={col.key}
          col={col} b={effectiveB} ri={ri} isSel={isSel} isEd={isCellEd}
          onBookingChange={effBChange}
          onBookingSave={effBSave}
          onFocusMe={() => setSelectedCell({ri, key:col.key})}
          onEnterEdit={() => enterEdit(ri, col.key)}
          onExitEdit={() => {
            setEditingCell(null);
            setSelectedCell({ri, key:col.key});
            if (!isRowEd) requestAnimationFrame(() => {
              document.querySelector(`[data-row="${ri}"][data-colkey="${col.key}"]`)?.focus();
            });
          }}
          onNavKey={(e) => handleKeyDown(e, ri, col)}
        />
      );
    }

    // Payment amount — uses PaymentCell; in row-edit mode uses draft and defers save
    if (col.payment) {
      return (
        <PaymentCell key={col.key}
          col={col} b={effectiveB} ri={ri} isSel={isSel} isEd={isCellEd}
          onFocusMe={() => setSelectedCell({ri, key:col.key})}
          onEnterEdit={() => enterEdit(ri, col.key)}
          onCommit={(val) => {
            effBChange(b.id, col.key, val ?? 0);
            if (!isRowEd) { onBookingSave(b.id, col.key, val, b); }
            setEditingCell(null);
          }}
          onEscape={() => {
            setEditingCell(null);
            setSelectedCell({ri, key:col.key});
            if (!isRowEd) requestAnimationFrame(() => document.querySelector(`[data-row="${ri}"][data-colkey="${col.key}"]`)?.focus());
          }}
          onNavKey={(e) => handleKeyDown(e, ri, col)}
        />
      );
    }

    // Number input (non-payment) — always visible as input when row is in edit mode
    if (col.input === 'number') {
      const isTrk = col.src === 'tracker';
      const raw   = isTrk ? effectiveTE[col.key] : effectiveB[col.key];
      const val   = Number(raw) || 0;
      const isEd  = isRowEd || isCellEd;
      return (
        <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
          {isEd ? (
            <input type="number" min="0" value={val||''}
              data-row={ri} data-colkey={col.key}
              onChange={(e) => {
                const isInt = col.key==='adults'||col.key==='children'||col.key==='tour_leaders';
                const raw = e.target.value;
                const v = raw==='' ? 0 : isInt ? (parseInt(raw,10)||0) : (parseFloat(raw)||0);
                isTrk ? effTChange(b.id,col.key,v) : effBChange(b.id,col.key,v);
              }}
              onBlur={(e) => {
                if (!isRowEd) {
                  const v = e.target.value==='' ? 0 : Number(e.target.value);
                  const snap = editSnapshotRef.current;
                  isTrk ? onTrackerSave(b.id,col.key,v,snap?.[col.key]) : onBookingSave(b.id,col.key,v,snap);
                  setEditingCell(null);
                }
              }}
              onKeyDown={(e) => handleKeyDown(e, ri, col)}
              className="tracker-input"
              style={{width:'100%',padding:'4px 6px',textAlign:'right'}}
            />
          ) : (
            <div tabIndex={0} data-row={ri} data-colkey={col.key}
              onFocus={() => setSelectedCell({ri, key:col.key})}
              onClick={() => enterEdit(ri, col.key)}
              onKeyDown={(e) => handleKeyDown(e, ri, col)}
              style={{...dispStyle(isSel), textAlign:'right'}}
            >
              {val ? (col.fmt==='currency' ? fmtMoney(val) : String(val)) : ''}
            </div>
          )}
        </td>
      );
    }

    // Partner name — uses autocomplete PartnerCell; in row-edit mode uses draft callbacks
    if (col.key === 'partner_name') {
      return (
        <PartnerCell key={col.key}
          col={col} b={effectiveB} ri={ri} isSel={isSel} isEd={isCellEd}
          onBookingChange={effBChange} onBookingSave={effBSave} onMultiSave={effMulti}
          onFocusMe={() => setSelectedCell({ri, key:col.key})}
          onEnterEdit={() => enterEdit(ri, col.key)}
          onExitEdit={() => {
            setEditingCell(null);
            setSelectedCell({ri, key:col.key});
            if (!isRowEd) requestAnimationFrame(() => document.querySelector(`[data-row="${ri}"][data-colkey="${col.key}"]`)?.focus());
          }}
          onNavKey={(e) => handleKeyDown(e, ri, col)}
          partners={partners || []}
        />
      );
    }

    // Text input — always visible as input when row is in edit mode
    const isTrk = col.src === 'tracker';
    const val   = isTrk ? (effectiveTE[col.key]||'') : (effectiveB[col.key]||'');
    const isCancelled = !isTrk && effectiveB.payment_status === 'Cancelled';
    const isPhone = col.key === 'guest_phone' || col.key === 'partner_phone';
    const phoneError = cellErrors[`${b.id}_${col.key}`] || '';
    const isEd = isRowEd || isCellEd;
    return (
      <td key={col.key} className="tracker-td tracker-td-editable" style={{...cs(col),padding:0}}>
        {isEd ? (
          <div style={{display:'flex',flexDirection:'column',padding:'2px 4px',gap:1}}>
            {isCancelled && col.key==='booking_name' && (
              <div style={{fontSize:'0.58rem',fontWeight:800,color:'#991b1b',letterSpacing:1}}>CANCELLED</div>
            )}
            <input type="text" value={val}
              data-row={ri} data-colkey={col.key}
              onChange={(e) => isTrk ? effTChange(b.id,col.key,e.target.value) : effBChange(b.id,col.key,e.target.value)}
              onBlur={(e) => {
                if (isPhone) {
                  const digits = e.target.value.replace(/\D/g, '');
                  const err = e.target.value && digits.length !== 10 ? 'Must be 10 digits' : '';
                  setCellErrors(prev => ({ ...prev, [`${b.id}_${col.key}`]: err }));
                }
                if (!isRowEd) {
                  const snap = editSnapshotRef.current;
                  isTrk ? onTrackerSave(b.id,col.key,e.target.value,snap?.[col.key]) : onBookingSave(b.id,col.key,e.target.value||null,snap);
                  setEditingCell(null);
                }
              }}
              onKeyDown={(e) => handleKeyDown(e, ri, col)}
              className="tracker-input"
              style={{width:'100%',padding:'4px 6px'}}
            />
            {phoneError && <div style={{fontSize:'0.62rem',color:'#dc2626',padding:'0 2px'}}>{phoneError}</div>}
          </div>
        ) : (
          <div tabIndex={0} data-row={ri} data-colkey={col.key}
            onFocus={() => setSelectedCell({ri, key:col.key})}
            onClick={() => enterEdit(ri, col.key)}
            onKeyDown={(e) => handleKeyDown(e, ri, col)}
            style={dispStyle(isSel)}
          >
            {isCancelled && col.key==='booking_name' && (
              <span style={{fontSize:'0.58rem',fontWeight:800,color:'#991b1b',letterSpacing:1,marginRight:4}}>CANCELLED</span>
            )}
            {val || <span style={{color:'var(--color-ink-faint)'}}>—</span>}
            {col.key==='booking_name' && isDinnerCruise && Number(b.food_jain) > 0 && (
              <span style={{marginLeft:6,fontSize:'0.6rem',fontWeight:700,color:'#92400e',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:4,padding:'1px 5px',whiteSpace:'nowrap',verticalAlign:'middle'}}>
                {b.food_jain} Jain
              </span>
            )}
            {phoneError && <div style={{fontSize:'0.62rem',color:'#dc2626'}}>{phoneError}</div>}
          </div>
        )}
      </td>
    );
  };

  // Guard navigation when a row is being edited
  const guardedNav = (fn) => (...args) => {
    if (editingRow !== null) { setUnsavedWarning({ onConfirm: () => { cancelRowEdit(); fn(...args); } }); return; }
    fn(...args);
  };

  return (
    <div onClick={() => { if (editingRow === null) { setSelectedCell(null); setEditingCell(null); } }}>
      {/* Unsaved-changes warning dialog */}
      {unsavedWarning && (
        <div style={{position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(10,22,40,0.45)'}}>
          <div style={{background:'#ffffff',borderRadius:14,padding:'28px 32px',maxWidth:380,boxShadow:'0 8px 32px rgba(10,22,40,0.28)',textAlign:'center'}}>
            <div style={{fontSize:'1.05rem',fontWeight:700,color:'#1a2a40',marginBottom:8}}>Unsaved changes</div>
            <div style={{fontSize:'0.85rem',color:'var(--color-ink-muted)',marginBottom:22}}>
              {unsavedWarning.onConfirm
                ? 'You have unsaved changes. Save or cancel before navigating away.'
                : 'You have unsaved changes. Save or cancel before editing another row.'}
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button
                onClick={() => setUnsavedWarning(null)}
                style={{background:'#0A1628',color:'#C9A84C',border:'none',borderRadius:8,padding:'8px 20px',fontWeight:700,fontSize:'0.85rem',cursor:'pointer'}}
              >
                Stay
              </button>
              {unsavedWarning.onConfirm && (
                <button
                  onClick={() => { const fn = unsavedWarning.onConfirm; setUnsavedWarning(null); fn(); }}
                  style={{background:'transparent',color:'var(--color-ink-muted)',border:'1px solid var(--color-border-strong)',borderRadius:8,padding:'8px 20px',fontWeight:600,fontSize:'0.85rem',cursor:'pointer'}}
                >
                  Leave anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button className="btn btn-gold btn-sm" onClick={(e)=>{e.stopPropagation();guardedNav(onAddBooking)();}}>+ Add Booking</button>
          {onViewSeating && (
            <button className="btn btn-outline btn-sm" onClick={(e)=>{e.stopPropagation();guardedNav(onViewSeating)();}}>⇢ Seating Sheet</button>
          )}
          {onViewReport && (
            <button className="btn btn-outline btn-sm" onClick={(e)=>{e.stopPropagation();guardedNav(onViewReport)();}}>⇢ Report</button>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button
            onClick={(e) => { e.stopPropagation(); if (undoCount > 0) onUndo(); }}
            disabled={!undoCount}
            title={undoCount ? `Undo last change (${undoCount} step${undoCount!==1?'s':''})` : 'Nothing to undo'}
            style={{
              background: 'none', border: 'none', cursor: undoCount ? 'pointer' : 'default',
              fontSize: '0.72rem', fontWeight: 600, padding: '4px 6px', borderRadius: 5,
              color: undoCount ? '#5a6a7a' : '#c0bab0',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { if (undoCount) e.currentTarget.style.color = '#C9A84C'; }}
            onMouseLeave={e => { e.currentTarget.style.color = undoCount ? '#5a6a7a' : '#c0bab0'; }}
          >
            ↩ Undo <span style={{opacity:0.65}}>⌘Z</span>
          </button>
          <button className="btn btn-outline btn-sm" onClick={(e)=>{e.stopPropagation();onExport();}}>↓ Export</button>
        </div>
      </div>

      {/* Pricing unavailable — pax/deck edits and new rows are refused until it loads */}
      {pricingStatus === 'error' && (
        <div style={{display:'flex',alignItems:'center',gap:10,background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:'0.8rem',fontWeight:600,color:'#991b1b'}} onClick={e=>e.stopPropagation()}>
          <AlertTriangle size={15} strokeWidth={1.75} aria-hidden="true" style={{flexShrink:0}} />
          <span style={{flex:1}}>Pricing couldn't be loaded. Pax and deck changes and new rows can't be saved until it does (FOC rows can).</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={onRetryPricing} style={{flexShrink:0}}>Retry</button>
        </div>
      )}
      {/* A row save refused because the price is unknown */}
      {rowSaveError && (
        <div style={{display:'flex',alignItems:'center',gap:10,background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:'0.8rem',fontWeight:600,color:'#991b1b'}} onClick={e=>e.stopPropagation()}>
          <AlertTriangle size={15} strokeWidth={1.75} aria-hidden="true" style={{flexShrink:0}} />
          <span style={{flex:1}}>{rowSaveError}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={()=>setRowSaveError('')} style={{flexShrink:0}}>Dismiss</button>
        </div>
      )}
      {/* Search + filters */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12}} onClick={e=>e.stopPropagation()}>
        <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search bookings…"
          style={{...fSel, flex:1, minWidth:160, maxWidth:260}}
        />
        <select value={deckFilter} onChange={e=>setDeckFilter(e.target.value)} style={deckFilter?fSelOn:fSel}>
          <option value="">All Decks</option>
          {DECK_OPTIONS.map(d=><option key={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={statusFilter?fSelOn:fSel}>
          <option value="">All Statuses</option>
          {PAYMENT_STATUS_OPTIONS.filter(Boolean).map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={partnerFilter} onChange={e=>setPartnerFilter(e.target.value)} style={partnerFilter?fSelOn:fSel}>
          <option value="">All Bookings</option>
          <option value="with">With Partner</option>
          <option value="without">Without Partner</option>
        </select>
        {hasFilter && (
          <button onClick={clearFilters}
            style={{...fSel,background:'#fef2f2',borderColor:'#fca5a5',color:'#991b1b'}}>
            ✕ Clear
          </button>
        )}
        <span style={{fontSize:'0.78rem',color:'var(--color-ink-muted)'}}>
          {hasFilter ? `Showing ${displayedBookings.length} of ${bookings.length}` : `${bookings.length} booking${bookings.length!==1?'s':''}`}
        </span>
      </div>

      {/* Table — always rendered so Add Row button in tfoot is always accessible */}
      <div className="tracker-table-wrap" onClick={e=>e.stopPropagation()}>
        <table className="tracker-table" style={{tableLayout:'fixed'}}>
          <colgroup>
            {TRACKER_COLS.map(col => <col key={col.key} style={{width:col.w}} />)}
          </colgroup>
          <thead>
            <tr>
              {TRACKER_COLS.map(col => {
                const bg = col.src==='tracker' ? trackerColor : col.input ? bookingColor : hdrColor;
                const isFilterActive = (col.key==='deck'&&deckFilter)||(col.key==='payment_status'&&statusFilter)||(col.key==='partner_name'&&partnerFilter);
                return (
                  <th key={col.key} style={{...cs(col), background:bg, color:'#ffffff', borderRight:'1px solid rgba(255,255,255,0.1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {col.label}
                    {FILTERABLE_KEYS.has(col.key) && (
                      <span style={{marginLeft:4,opacity:isFilterActive?1:0.35,fontSize:'0.55rem'}}>▼</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayedBookings.length === 0 && !newRow ? (
              <tr>
                <td colSpan={TRACKER_COLS.length} style={{textAlign:'center',padding:'48px 24px',color:'var(--color-ink-faint)',background:'#ffffff'}}>
                  <div style={{fontSize:'0.9rem',fontWeight:600,color:NAVY,marginBottom:4}}>No bookings</div>
                  <div style={{fontSize:'0.8rem'}}>{hasFilter ? 'No bookings match the current filter.' : `No ${cruise} bookings for this date.`}</div>
                </td>
              </tr>
            ) : displayedBookings.map((b, ri) => {
              const te = trackerData[b.id] || EMPTY_TRACKER;
              const rowActive  = selectedCell?.ri===ri || editingCell?.ri===ri;
              const rowEditing = editingRow === ri;
              return (
                <tr key={b.id}
                  className={[getRowClass(b), ri%2===1?'tracker-row-alt':'', rowActive?'tracker-row-active':'', rowEditing?'tracker-row-editing':''].filter(Boolean).join(' ')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editingRow !== null && editingRow !== ri) {
                      setUnsavedWarning({ targetRi: ri });
                    }
                  }}
                >
                  {TRACKER_COLS.map(col => renderCell(col, b, ri, te))}
                </tr>
              );
            })}
            {newRow && (
              <tr data-newrow="true" style={{background:'#fffde7',outline:'2px solid #C9A84C66',outlineOffset:-1}}>
                {TRACKER_COLS.map(col => renderNewRowCell(col))}
              </tr>
            )}
            {newRow && isDinnerCruise && (() => {
              const req = (Number(newRow.adults)||0) + (Number(newRow.children)||0);
              const tot = (Number(newRow.food_non_veg)||0) + (Number(newRow.food_veg)||0) + (Number(newRow.food_jain)||0);
              const ok = tot === req;
              const foodInput = (key, label) => (
                <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                  <span style={{fontSize:'0.72rem',color:'var(--color-ink-muted)',fontWeight:600}}>{label}</span>
                  <input type="number" min="0" value={newRow[key]}
                    onChange={e => setNewRow(r => ({...r, [key]: e.target.value === '' ? '' : Number(e.target.value)}))}
                    style={{width:48,padding:'3px 6px',border:'1.5px solid var(--color-border-strong)',borderRadius:6,fontSize:'0.78rem'}} />
                </span>
              );
              return (
                <tr data-newrow="true" style={{background:'#fffde7',outline:'2px solid #C9A84C66',outlineOffset:-1}}>
                  <td colSpan={TRACKER_COLS.length} style={{padding:'6px 10px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                      <span style={{fontSize:'0.78rem',fontWeight:700,color:NAVY}}>Food preferences (buffet)</span>
                      {foodInput('food_non_veg','Non-veg')}
                      {foodInput('food_veg','Veg')}
                      {foodInput('food_jain','Jain')}
                      <span style={{fontSize:'0.78rem',fontWeight:600,color: ok ? 'var(--color-ink-muted)' : '#dc2626'}}>
                        {ok ? `Total ${tot} of ${req} (adults + children).`
                            : `Must total ${req} (adults + children). Currently ${tot}.`}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })()}
          </tbody>
          <tfoot>
            {/* Add Row button row */}
            <tr>
              <td colSpan={TRACKER_COLS.length} style={{padding:'5px 8px',background:'var(--color-sunken)',borderTop:'2px solid var(--color-border)'}}>
                <button
                  className="btn btn-outline btn-sm"
                  disabled={!!newRow}
                  onClick={(e) => { e.stopPropagation(); setNewRow(INIT_NEW_ROW); }}
                  style={{fontSize:'0.75rem', padding:'4px 12px'}}
                >
                  + Add Row
                  <span style={{opacity:0.55, fontSize:'0.7rem', marginLeft:6}}>(Shift+N)</span>
                </button>
              </td>
            </tr>
            {/* Summary row */}
            <tr>
              {TRACKER_COLS.map(col => (
                <td key={col.key} className="tracker-td tracker-td-summary" style={cs(col)}>{getSummaryCell(col)}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Stats below table */}
      <div style={{marginTop:16,display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-start'}}>
        {deckStats.map(ds => (
          <div key={ds.label} style={{background:NAVY,borderRadius:10,padding:'10px 16px',color:'#ffffff',minWidth:160}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'#C9A84C',marginBottom:4,letterSpacing:'0.4px',textTransform:'uppercase'}}>{ds.label} Deck</div>
            <div style={{fontSize:'0.84rem',fontWeight:600}}>{ds.adults} Adults + {ds.children} Children</div>
            <div style={{fontSize:'0.73rem',color:'#a0b0c0',marginTop:2}}>Total: {ds.adults+ds.children} people</div>
          </div>
        ))}
        {deckStats.length > 0 && (
          <div style={{background:'#1a3a5c',borderRadius:10,padding:'10px 16px',color:'#ffffff',minWidth:160,borderLeft:'3px solid #C9A84C'}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'#C9A84C',marginBottom:4,letterSpacing:'0.4px',textTransform:'uppercase'}}>Total People</div>
            <div style={{fontSize:'0.84rem',fontWeight:600}}>{tots.adults} Adults + {tots.children} Children</div>
            <div style={{fontSize:'0.73rem',color:'#a0b0c0',marginTop:2}}>Total: {tots.adults+tots.children} people</div>
          </div>
        )}
        {/* Food preferences — Dinner Cruise only; hidden when there's no food data */}
        {foodHasData && (
          <div style={{background:NAVY,borderRadius:10,padding:'10px 16px',color:'#ffffff',minWidth:160}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'#C9A84C',marginBottom:4,letterSpacing:'0.4px',textTransform:'uppercase'}}>Food</div>
            <div style={{fontSize:'0.84rem',fontWeight:600}}>{foodTotals.nonVeg} Non-veg · {foodTotals.veg} Veg · {foodTotals.jain} Jain</div>
          </div>
        )}
        <div style={{display:'flex',gap:20,flexWrap:'wrap',alignItems:'flex-start',paddingLeft:deckStats.length?12:0,borderLeft:deckStats.length?'1px solid var(--color-border)':'none'}}>
          {[{label:'Collection',val:tots.total_amount,gold:true},{label:'TAC',val:tots.tac},{label:'Net Collection',val:PAYMENT_AMOUNT_KEYS.reduce((s,k)=>s+(Number(tots[k])||0),0)-tots.driver_commission}].map(({label,val,gold})=>(
            <div key={label} className="tracker-stat">
              <span className="tracker-stat-label">{label}</span>
              <span className="tracker-stat-value" style={gold?{color:'#C9A84C'}:{}}>{fmtMoney(val)}</span>
            </div>
          ))}
          {PAYMENT_MODES.map((mode,i) => {
            const v = tots[PAYMENT_MODE_TO_KEY[mode]]; if (!v) return null;
            return (
              <div key={mode} className="tracker-stat" style={i===0?{borderLeft:'1px solid var(--color-border)',paddingLeft:16}:{}}>
                <span className="tracker-stat-label">{mode}</span>
                <span className="tracker-stat-value" style={{fontSize:'0.88rem',color:'#15803d'}}>{fmtMoney(v)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {refundTarget && (
        <RefundModal
          booking={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSaved={() => { setRefundTarget(null); onRefresh(); }}
        />
      )}
      {paymentTarget && (
        <RecordPaymentModal
          booking={paymentTarget}
          onClose={() => setPaymentTarget(null)}
          onSaved={() => { setPaymentTarget(null); onRefresh(); }}
        />
      )}
    </div>
  );
}


// ─── Arrow button ─────────────────────────────────────────────────────────────
const arrowBtn = {width:38,height:38,background:NAVY,color:'#ffffff',border:'none',borderRadius:8,fontSize:'1.1rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'background 0.15s'};

const SAVE_STATUS_STYLE = {
  idle:   null,
  saving: {bg:'var(--color-sunken)',border:'var(--color-border-strong)',color:'var(--color-ink-muted)',text:'Saving…'},
  saved:  {bg:'#f0fdf4',border:'#86efac',color:'#15803d',text:'✓ Saved'},
  error:  {bg:'#fef2f2',border:'#fca5a5',color:'#991b1b',text:'Error saving'},
  // A save this screen refused itself (price unknown) — same look, longer
  // hold, and the text comes from saveErrorText so it can name the slot.
  refused: {bg:'#fef2f2',border:'#fca5a5',color:'#991b1b',text:'Not saved'},
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function DailyTracker({ onNavigate, initialDate, initialCruise, onStateChange }) {
  const [inputDate, setInputDate] = useState(() => {
    if (initialDate) {
      // initialDate arrives as DD-MM-YYYY (activeDate format); convert to YYYY-MM-DD
      const parts = initialDate.split('-');
      if (parts.length === 3 && parts[2].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return todayISO();
  });
  const [activeTab, setActiveTab] = useState(initialCruise || '');
  const [bookings, setBookings]       = useState([]);
  const [trackerData, setTrackerData] = useState({});
  const [loading, setLoading]         = useState(false);
  const [saveStatus, setSaveStatus]   = useState('idle');
  const [saveErrorText, setSaveErrorText] = useState('');
  // Live pricing lookup (hooks/usePricing.js). null while loading or after a
  // failed read; never a hardcoded fallback. Every rate goes through ratesFor().
  const { lookup: pricing, status: pricingStatus, retry: retryPricing } = usePricingLookup();
  const [partners, setPartners]       = useState([]);
  const [invoiceMap, setInvoiceMap]   = useState(new Map());
  const [invoiceModal, setInvoiceModal] = useState(null); // { booking, existingInvoice? }
  const [invoicePrint, setInvoicePrint] = useState(null); // invoiceData
  const [sendTarget, setSendTarget]     = useState(null); // booking to send via WhatsApp

  const { settings: cruiseSettings } = useCruiseSettings();

  const [undoStack, setUndoStack]   = useState([]);
  const [undoToast, setUndoToast]   = useState(null);

  const saveTimerRef      = useRef(null);
  const undoToastTimer    = useRef(null);
  const bookingsRef       = useRef([]);
  const trackerDataRef    = useRef({});
  const pricingRef        = useRef(null);
  const pricingStatusRef  = useRef('loading');
  const recalcRef         = useRef({});
  const undoStackRef      = useRef([]);

  useEffect(() => { bookingsRef.current    = bookings;     }, [bookings]);
  useEffect(() => { trackerDataRef.current = trackerData;  }, [trackerData]);
  useEffect(() => { pricingRef.current     = pricing;      }, [pricing]);
  useEffect(() => { pricingStatusRef.current = pricingStatus; }, [pricingStatus]);
  useEffect(() => { undoStackRef.current   = undoStack;    }, [undoStack]);

  // Reset undo history when date or cruise tab changes
  useEffect(() => { setUndoStack([]); }, [inputDate, activeTab]);

  // Set default tab once cruise settings load
  useEffect(() => {
    if (!activeTab && cruiseSettings.length > 0) {
      setActiveTab(cruiseSettings[0].cruise_name);
    }
  }, [cruiseSettings, activeTab]);

  useEffect(() => {
    // Live partners only: this list feeds the partner cell dropdown, the Add Row
    // cell and resolvePartnerId, so an archived partner is neither suggested nor
    // resolved by name. Bookings already linked keep their partner_id untouched.
    supabase.from('partners')
      .select('id, name, phone_number, preferred_payment_mode, preferred_payment_status')
      .is('archived_at', null)
      .order('name')
      .then(({ data }) => { if (data) setPartners(data); });
  }, []);

  const markSaving = () => { setSaveStatus('saving'); if(saveTimerRef.current)clearTimeout(saveTimerRef.current); };
  const markSaved  = () => { setSaveStatus('saved');  saveTimerRef.current=setTimeout(()=>setSaveStatus('idle'),3000); };
  const markError  = () => { setSaveStatus('error');  saveTimerRef.current=setTimeout(()=>setSaveStatus('idle'),3000); };
  // A save THIS screen refused (not a DB failure): show why, hold it longer.
  const markRefused = (text) => {
    setSaveErrorText(text); setSaveStatus('refused');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { setSaveStatus('idle'); setSaveErrorText(''); }, 6000);
  };

  const pushUndo = useCallback((entry) => {
    setUndoStack(prev => [...prev.slice(-19), entry]);
  }, []);

  const showUndoToast = (msg) => {
    setUndoToast(msg);
    if (undoToastTimer.current) clearTimeout(undoToastTimer.current);
    undoToastTimer.current = setTimeout(() => setUndoToast(null), 2000);
  };

  const performUndo = useCallback(async () => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));

    if (entry.type === 'booking') {
      setBookings(prev => prev.map(b => b.id === entry.id ? { ...b, ...entry.prevPayload } : b));
      await supabase.from('bookings').update(entry.prevPayload).eq('id', entry.id);
      const pv = entry.prevPayload[entry.key];
      showUndoToast(`Undone: ${FIELD_LABELS[entry.key]||entry.key} → ${fmtUndoVal(entry.key, pv)}`);
    } else if (entry.type === 'tracker') {
      setTrackerData(prev => ({
        ...prev, [entry.id]: { ...(prev[entry.id]||EMPTY_TRACKER), [entry.key]: entry.prevVal },
      }));
      const te = trackerDataRef.current[entry.id];
      if (te?.dbId) await supabase.from('tracker_entries').update({ [entry.key]: entry.prevVal }).eq('id', te.dbId);
      showUndoToast(`Undone: ${FIELD_LABELS[entry.key]||entry.key} → ${fmtUndoVal(entry.key, entry.prevVal)}`);
    } else if (entry.type === 'booking_multi') {
      setBookings(prev => prev.map(b => b.id === entry.id ? { ...b, ...entry.prevFields } : b));
      await supabase.from('bookings').update(entry.prevFields).eq('id', entry.id);
      showUndoToast('Undone: Partner selection reverted');
    } else if (entry.type === 'new_booking') {
      setBookings(prev => prev.filter(b => b.id !== entry.id));
      await supabase.from('bookings').delete().eq('id', entry.id);
      showUndoToast('Undone: New booking removed');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cmd/Ctrl+Z — only fires when no input/textarea is active (native undo unaffected)
  useEffect(() => {
    const handler = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'z') return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      performUndo();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [performUndo]);

  const shiftDay = (n) => { const d=new Date(inputDate); d.setDate(d.getDate()+n); setInputDate(d.toISOString().slice(0,10)); };

  // Sync URL when date or cruise tab changes
  useEffect(() => {
    if (!inputDate || !activeTab) return;
    onStateChange?.(toDisplayDate(inputDate), activeTab);
  }, [inputDate, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Realtime sync: bookings table ───────────────────────────────────────────
  useEffect(() => {
    const displayDate = toDisplayDate(inputDate);
    if (!displayDate) return;
    const ch = supabase
      .channel(`tracker-bk-${displayDate}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `date=eq.${displayDate}` },
        ({ eventType, new: n, old: o }) => {
          if (eventType === 'INSERT') {
            setBookings(prev => prev.some(b => b.id === n.id) ? prev : [...prev, n]);
          } else if (eventType === 'UPDATE') {
            setBookings(prev => prev.map(b => b.id === n.id ? { ...b, ...n } : b));
          } else if (eventType === 'DELETE') {
            setBookings(prev => prev.filter(b => b.id !== o.id));
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [inputDate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data:bData } = await supabase.from('bookings').select('*').eq('date', toDisplayDate(inputDate)).order('created_at');
    let rows = bData || [];

    rows = rows.map(b => {
      const allZero = PAYMENT_AMOUNT_KEYS.every(k => !b[k] || Number(b[k])===0);
      if (allZero && b.payment_mode && PAYMENT_MODE_TO_KEY[b.payment_mode]) {
        const net = (Number(b.total_amount)||0)-(Number(b.tac)||0);
        return { ...b, [PAYMENT_MODE_TO_KEY[b.payment_mode]]: net };
      }
      return b;
    });

    let tRows = [];
    if (rows.length) {
      const { data:te } = await supabase.from('tracker_entries').select('*').in('booking_id', rows.map(b=>b.id));
      tRows = te || [];
    }
    const refs = rows.length ? await getBookingReferences(rows.map(b => b.id)) : {};
    rows = rows.map(b => ({ ...b, booking_ref: refs[b.id] || '' }));
    setBookings(rows);
    const tMap = {};
    rows.forEach(b => { tMap[b.id] = {...EMPTY_TRACKER}; });
    tRows.forEach(te => {
      if (tMap[te.booking_id]) tMap[te.booking_id] = {dbId:te.id,ticket_sent:te.ticket_sent||false,notes:te.notes||'',driver_commission:te.driver_commission||0,checked:te.checked||false};
    });
    setTrackerData(tMap);

    if (rows.length > 0) {
      const { data: invData } = await supabase.from('invoices')
        .select('booking_id, invoice_number, invoice_date, customer_name, customer_phone')
        .in('booking_id', rows.map(b => b.id));
      const iMap = new Map();
      (invData || []).forEach(i => iMap.set(i.booking_id, i));
      setInvoiceMap(iMap);
    } else {
      setInvoiceMap(new Map());
    }

    setLoading(false);
  }, [inputDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleBookingChange = (bookingId, field, value) => {
    // recalcRef must be set SYNCHRONOUSLY here, before setBookings batches the update,
    // so handleBookingSave (called immediately after) can read the derived values.
    const bCur = bookingsRef.current.find(b => b.id === bookingId);
    if (bCur) {
      if (field === 'payment_status' && value === 'FOC') {
        recalcRef.current[bookingId] = { total_amount:0,tac:0,net_total:0,...Object.fromEntries(PAYMENT_AMOUNT_KEYS.map(k=>[k,0])) };
      } else if (field === 'adults' || field === 'children') {
        const a = field==='adults' ? (Number(value)||0) : (Number(bCur.adults)||0);
        const c = field==='children' ? (Number(value)||0) : (Number(bCur.children)||0);
        const rates = ratesFor(pricingRef.current, bCur.cruise_type, bCur.deck);
        if (rates) {
          const tot = a*rates.adult + c*rates.child;
          const tac = (bCur.partner_name&&bCur.partner_name.trim()) ? Math.round(tot*TAC_RATE) : 0;
          recalcRef.current[bookingId] = { total_amount:tot, tac, net_total:tot-tac };
        }
      } else if (field === 'deck') {
        const a = Number(bCur.adults)||0;
        const c = Number(bCur.children)||0;
        const rates = ratesFor(pricingRef.current, bCur.cruise_type, value);
        if (rates) {
          const tot = a*rates.adult + c*rates.child;
          const tac = (bCur.partner_name&&bCur.partner_name.trim()) ? Math.round(tot*TAC_RATE) : 0;
          recalcRef.current[bookingId] = { total_amount:tot, tac, net_total:tot-tac };
        }
      } else if (field === 'partner_name') {
        const tot = Number(bCur.total_amount)||0;
        const tac = (value&&value.trim()) ? Math.round(tot*TAC_RATE) : 0;
        recalcRef.current[bookingId] = { tac, net_total:tot-tac };
      }
    }

    setBookings(prev => prev.map(b => {
      if (b.id !== bookingId) return b;
      const u = { ...b, [field]: value };

      if (field === 'payment_status' && value === 'FOC') {
        u.total_amount=0; u.tac=0; u.net_total=0;
        PAYMENT_AMOUNT_KEYS.forEach(k=>{ u[k]=0; });
        return u;
      }
      if (field === 'adults' || field === 'children') {
        const a = field==='adults' ? (Number(value)||0) : (Number(b.adults)||0);
        const c = field==='children' ? (Number(value)||0) : (Number(b.children)||0);
        const rates = ratesFor(pricingRef.current, b.cruise_type, b.deck);
        if (rates) {
          const tot = a*rates.adult + c*rates.child;
          const tac = (b.partner_name&&b.partner_name.trim()) ? Math.round(tot*TAC_RATE) : 0;
          u.total_amount=tot; u.tac=tac; u.net_total=tot-tac;
        }
      }
      if (field === 'deck') {
        const a = Number(b.adults)||0;
        const c = Number(b.children)||0;
        const rates = ratesFor(pricingRef.current, b.cruise_type, value);
        if (rates) {
          const tot = a*rates.adult + c*rates.child;
          const tac = (b.partner_name&&b.partner_name.trim()) ? Math.round(tot*TAC_RATE) : 0;
          u.total_amount=tot; u.tac=tac; u.net_total=tot-tac;
        }
      }
      if (field === 'partner_name') {
        const tot = Number(b.total_amount)||0;
        const tac = (value&&value.trim()) ? Math.round(tot*TAC_RATE) : 0;
        u.tac=tac; u.net_total=tot-tac;
      }
      if (field === 'total_amount' || field === 'tac') {
        const tot = field==='total_amount' ? (Number(value)||0) : (Number(b.total_amount)||0);
        const tac = field==='tac'          ? (Number(value)||0) : (Number(b.tac)||0);
        u.net_total = tot - tac;
      }
      return u;
    }));
  };

  const handleBookingSave = async (bookingId, field, value, prevSnapshot) => {
    // Hard stop: a pax or deck edit on a non-FOC booking needs the live rate
    // card to recompute the total. handleBookingChange had none (recalcRef is
    // empty), so saving now would write the new pax against the OLD total.
    // Refuse, put the field back to what it was, and say why. FOC exempt.
    if (PRICE_FIELDS.has(field)) {
      const bCur = bookingsRef.current.find(b => b.id === bookingId);
      const status = bCur?.payment_status;
      const deck = field === 'deck' ? value : bCur?.deck;
      if (bCur && status !== 'FOC' && !ratesFor(pricingRef.current, bCur.cruise_type, deck)) {
        const prevVal = prevSnapshot ? prevSnapshot[field] : bCur[field];
        setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, [field]: prevVal } : b));
        delete recalcRef.current[bookingId];
        markRefused(priceRefusalMessage(pricingRef.current, pricingStatusRef.current, bCur.cruise_type, deck, 'The change'));
        return;
      }
    }

    markSaving();
    const payload = { [field]: value };

    if (field==='adults'||field==='children'||field==='deck'||field==='partner_name'||(field==='payment_status'&&value==='FOC')) {
      const r = recalcRef.current[bookingId];
      if (r) { Object.assign(payload, r); delete recalcRef.current[bookingId]; }
    }

    if (field==='total_amount'||field==='tac') {
      const bk = bookingsRef.current.find(b=>b.id===bookingId);
      if (bk) {
        const tot = field==='total_amount'?(Number(value)||0):(Number(bk.total_amount)||0);
        const tac = field==='tac'         ?(Number(value)||0):(Number(bk.tac)||0);
        payload.net_total = tot - tac;
      }
    }

    // Build undo entry: prevPayload mirrors all keys in payload, values from snapshot
    if (prevSnapshot) {
      const prevPayload = {};
      Object.keys(payload).forEach(k => { prevPayload[k] = prevSnapshot[k] ?? null; });
      const changed = Object.keys(payload).some(k => String(payload[k]) !== String(prevSnapshot[k]));
      if (changed) pushUndo({ type: 'booking', id: bookingId, key: field, prevPayload });
    }

    const { error } = await supabase.from('bookings').update(payload).eq('id', bookingId);
    if (error) { markError(); return; }
    markSaved();

    // When a booking is cancelled, remove it from seating assignments and boarded status
    if (field === 'payment_status' && value === 'Cancelled') {
      const { data: assigns } = await supabase
        .from('seating_assignments').select('table_name, date, cruise_type').eq('booking_id', bookingId);
      const deleteOps = [supabase.from('seating_assignments').delete().eq('booking_id', bookingId)];
      if (assigns?.length) {
        assigns.forEach(a => deleteOps.push(
          supabase.from('boarded_status').delete()
            .eq('date', a.date).eq('cruise_type', a.cruise_type).eq('table_name', a.table_name)
        ));
      }
      await Promise.all(deleteOps);
    }
  };

  const handleTrackerChange = (bookingId, field, value) => {
    setTrackerData(prev => ({ ...prev, [bookingId]: { ...(prev[bookingId]||EMPTY_TRACKER), [field]:value } }));
  };

  const handleTrackerSave = async (bookingId, field, value, prevVal) => {
    markSaving();
    try {
      const entry = trackerData[bookingId] || EMPTY_TRACKER;
      if (entry.dbId) {
        await supabase.from('tracker_entries').update({[field]:value,updated_at:new Date().toISOString()}).eq('id',entry.dbId);
      } else {
        const { data } = await supabase.from('tracker_entries').insert({booking_id:bookingId,[field]:value}).select('id').single();
        if (data?.id) setTrackerData(prev=>({...prev,[bookingId]:{...(prev[bookingId]||EMPTY_TRACKER),dbId:data.id}}));
      }
      if (prevVal !== undefined && String(prevVal) !== String(value)) {
        pushUndo({ type: 'tracker', id: bookingId, key: field, prevVal });
      }
      markSaved();
    } catch { markError(); }
  };

  // Save all changes for a row at once (used by row-level Save button)
  const handleRowSave = useCallback(async (bookingId, bookingPayload, trackerPayload, bookingSnapshot, trackerSnapshot) => {
    markSaving();
    try {
      if (Object.keys(bookingPayload).length > 0) {
        const prevPayload = {};
        Object.keys(bookingPayload).forEach(k => { prevPayload[k] = bookingSnapshot[k] ?? null; });
        if (Object.keys(prevPayload).some(k => String(bookingPayload[k]) !== String(prevPayload[k]))) {
          pushUndo({ type: 'booking_multi', id: bookingId, prevFields: prevPayload });
        }
        console.log('[handleRowSave] bookingId:', bookingId, 'payload:', bookingPayload);
        const { error } = await supabase.from('bookings').update(bookingPayload).eq('id', bookingId);
        if (error) { markError(); return; }
        if (bookingPayload.payment_status === 'Cancelled') {
          const { data: assigns } = await supabase.from('seating_assignments').select('table_name,date,cruise_type').eq('booking_id', bookingId);
          const ops = [supabase.from('seating_assignments').delete().eq('booking_id', bookingId)];
          if (assigns?.length) assigns.forEach(a => ops.push(
            supabase.from('boarded_status').delete().eq('date',a.date).eq('cruise_type',a.cruise_type).eq('table_name',a.table_name)
          ));
          await Promise.all(ops);
        }
      }
      if (Object.keys(trackerPayload).length > 0) {
        const entry = trackerDataRef.current[bookingId] || EMPTY_TRACKER;
        if (entry.dbId) {
          await supabase.from('tracker_entries').update({ ...trackerPayload, updated_at: new Date().toISOString() }).eq('id', entry.dbId);
        } else {
          const { data } = await supabase.from('tracker_entries').insert({ booking_id: bookingId, ...trackerPayload }).select('id').single();
          if (data?.id) setTrackerData(prev => ({ ...prev, [bookingId]: { ...(prev[bookingId]||EMPTY_TRACKER), dbId: data.id } }));
        }
      }
      markSaved();
    } catch { markError(); }
  }, [pushUndo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save multiple booking fields in one Supabase call (used by PartnerCell)
  const handleMultiSave = useCallback(async (bookingId, fields, prevFields) => {
    markSaving();
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...fields } : b));
    if (prevFields && Object.keys(prevFields).length) {
      pushUndo({ type: 'booking_multi', id: bookingId, prevFields });
    }
    const { error } = await supabase.from('bookings').update(fields).eq('id', bookingId);
    if (error) markError(); else markSaved();
  }, [pushUndo]); // eslint-disable-line react-hooks/exhaustive-deps

  const [hasUnsavedRow, setHasUnsavedRow] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState(null);

  const switchTab = (tab) => {
    if (hasUnsavedRow) { setPendingTabSwitch(tab); return; }
    setActiveTab(tab);
  };

  const TABS = cruiseSettings.map(s => s.cruise_name);
  const tabBookings = bookings.filter(b => b.cruise_type === activeTab);
  const statusStyle = SAVE_STATUS_STYLE[saveStatus];

  const getCruiseColor = (tabName) => {
    const idx = cruiseSettings.findIndex(s => s.cruise_name === tabName);
    return idx >= 0 ? CRUISE_COLOR_PALETTE[idx % CRUISE_COLOR_PALETTE.length] : CRUISE_COLOR_PALETTE[0];
  };

  const handleDownloadTicket = useCallback(async (b) => {
    try {
      const reference = b.booking_ref || await getBookingReference(b.id);
      await downloadTicket(b, reference);
      handleTrackerChange(b.id, 'ticket_sent', true);
      handleTrackerSave(b.id, 'ticket_sent', true, false);
    } catch (err) {
      console.error('Ticket download failed:', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {undoToast && (
        <div style={{position:'fixed',top:16,left:'50%',transform:'translateX(-50%)',zIndex:1100,
          background:'#0A1628',color:'#C9A84C',padding:'9px 18px',borderRadius:9,
          fontSize:'0.82rem',fontWeight:600,boxShadow:'0 4px 16px rgba(10,22,40,0.28)',
          whiteSpace:'nowrap',letterSpacing:'0.1px',pointerEvents:'none'}}>
          {undoToast}
        </div>
      )}
      {statusStyle && (
        <div style={{position:'fixed',top:16,right:20,zIndex:1000,background:statusStyle.bg,border:`1px solid ${statusStyle.border}`,color:statusStyle.color,padding:'6px 14px',borderRadius:8,fontSize:'0.8rem',fontWeight:600,boxShadow:'0 2px 8px rgba(10,22,40,0.14)',display:'inline-flex',alignItems:'center',gap:6}}>
          {(saveStatus === 'error' || saveStatus === 'refused') && <AlertTriangle size={15} strokeWidth={1.75} aria-hidden="true" />}
          {saveStatus === 'refused' && saveErrorText ? saveErrorText : statusStyle.text}
        </div>
      )}

      {/* Tab-switch / navigation warning when a row is being edited */}
      {pendingTabSwitch && (
        <div style={{position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(10,22,40,0.45)'}}>
          <div style={{background:'#ffffff',borderRadius:14,padding:'28px 32px',maxWidth:380,boxShadow:'0 8px 32px rgba(10,22,40,0.28)',textAlign:'center'}}>
            <div style={{fontSize:'1.05rem',fontWeight:700,color:'#1a2a40',marginBottom:8}}>Unsaved changes</div>
            <div style={{fontSize:'0.85rem',color:'var(--color-ink-muted)',marginBottom:22}}>You have unsaved changes. Are you sure you want to leave?</div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={() => setPendingTabSwitch(null)}
                style={{background:'#0A1628',color:'#C9A84C',border:'none',borderRadius:8,padding:'8px 20px',fontWeight:700,fontSize:'0.85rem',cursor:'pointer'}}>
                Stay
              </button>
              <button onClick={() => {
                const dest = pendingTabSwitch;
                setPendingTabSwitch(null);
                setHasUnsavedRow(false);
                if (dest === '_back_operations') onNavigate('operations');
                else setActiveTab(dest);
              }}
                style={{background:'transparent',color:'var(--color-ink-muted)',border:'1px solid var(--color-border-strong)',borderRadius:8,padding:'8px 20px',fontWeight:600,fontSize:'0.85rem',cursor:'pointer'}}>
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Invoice generation modal */}
      {invoiceModal && (
        <InvoiceModal
          booking={invoiceModal.booking}
          existingInvoice={invoiceModal.existingInvoice}
          onClose={() => setInvoiceModal(null)}
          onGenerated={(invoiceData) => {
            setInvoiceMap(prev => {
              const next = new Map(prev);
              next.set(invoiceModal.booking.id, { booking_id: invoiceModal.booking.id, invoice_number: invoiceData.invoiceNumber, invoice_date: invoiceData.invoiceDate, customer_name: invoiceData.customerName, customer_phone: invoiceData.customerPhone });
              return next;
            });
            setInvoiceModal(null);
            setInvoicePrint(invoiceData);
          }}
        />
      )}

      {/* Invoice print preview */}
      {invoicePrint && (
        <InvoicePrint
          invoiceData={invoicePrint}
          onClose={() => setInvoicePrint(null)}
        />
      )}

      {/* Send ticket via WhatsApp — reuses the same modal as ViewBookings */}
      {sendTarget && (
        <SendTicketModal
          booking={sendTarget}
          reference={sendTarget.booking_ref || null}
          onClose={() => setSendTarget(null)}
        />
      )}

      <div className="back-row">
        <button className="back-btn" onClick={() => { if (hasUnsavedRow) { setPendingTabSwitch('_back_operations'); } else { onNavigate('operations'); } }}>← Operations</button>
        <h2 style={{fontSize:'1rem',fontWeight:700,color:NAVY}}>Daily Tracker</h2>
      </div>

      {/* Date navigation */}
      <div style={{background:'#ffffff',border:'1px solid var(--color-border)',borderRadius:14,padding:'16px 20px',marginBottom:20,boxShadow:'0 1px 6px rgba(10,22,40,0.06)'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button style={arrowBtn} onClick={()=>shiftDay(-1)} onMouseEnter={e=>e.currentTarget.style.background='#C9A84C'} onMouseLeave={e=>e.currentTarget.style.background=NAVY} aria-label="Previous day">←</button>
          <input type="date" value={inputDate} onChange={e=>setInputDate(e.target.value)} style={{background:'var(--color-sunken)',border:'1.5px solid var(--color-border-strong)',borderRadius:8,padding:'8px 12px',color:NAVY,fontSize:'0.95rem',fontWeight:600,outline:'none',flex:1,maxWidth:200,colorScheme:'light'}} />
          <button style={arrowBtn} onClick={()=>shiftDay(1)} onMouseEnter={e=>e.currentTarget.style.background='#C9A84C'} onMouseLeave={e=>e.currentTarget.style.background=NAVY} aria-label="Next day">→</button>
        </div>
        {inputDate && <p style={{marginTop:10,fontSize:'1.2rem',fontWeight:700,color:'#C9A84C',letterSpacing:'-0.3px'}}>{formatDateWords(inputDate)}</p>}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:20,borderBottom:'2px solid var(--color-border)'}}>
        {TABS.map(tab => {
          const count    = bookings.filter(b=>b.cruise_type===tab).length;
          const isActive = activeTab===tab;
          const tabClr   = getCruiseColor(tab)?.header || NAVY;
          return (
            <button key={tab} onClick={()=>switchTab(tab)} style={{padding:'9px 16px',background:isActive?tabClr:'transparent',color:isActive?'#ffffff':'var(--color-ink-muted)',border:'none',borderRadius:'8px 8px 0 0',fontSize:'0.82rem',fontWeight:isActive?700:600,cursor:'pointer',transition:'all 0.15s',marginBottom:-2,borderBottom:isActive?`2px solid ${tabClr}`:'2px solid transparent',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
              {tab}
              {count!=null && <span style={{background:isActive?'rgba(255,255,255,0.25)':'var(--color-border)',color:isActive?'#ffffff':'var(--color-ink-muted)',borderRadius:20,padding:'1px 7px',fontSize:'0.7rem',fontWeight:700}}>{loading?'…':count}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:'60px 0',color:'var(--color-ink-faint)',fontSize:'0.9rem'}}>Loading…</div>
      ) : (
        <CruiseTab
          cruise={activeTab}
          bookings={tabBookings}
          trackerData={trackerData}
          onBookingChange={handleBookingChange}
          onBookingSave={handleBookingSave}
          onTrackerChange={handleTrackerChange}
          onTrackerSave={handleTrackerSave}
          onExport={() => exportCruiseTab(inputDate, activeTab, tabBookings, trackerData)}
          onAddBooking={() => onNavigate('add-inquiry-prefill', toDisplayDate(inputDate), {cruise_type:activeTab, _return_to:'daily-tracker'})}
          onViewSeating={() => onNavigate('seating-sheet', toDisplayDate(inputDate), {cruise_type:activeTab})}
          onViewReport={() => onNavigate('reports', toDisplayDate(inputDate), { _from_tracker: true })}
          colors={getCruiseColor(activeTab)}
          currentDate={inputDate}
          partners={partners}
          pricing={pricing}
          pricingStatus={pricingStatus}
          onRetryPricing={retryPricing}
          onRefresh={fetchAll}
          onMultiSave={handleMultiSave}
          onPushUndo={pushUndo}
          undoCount={undoStack.length}
          onUndo={performUndo}
          cruiseSetting={cruiseSettings.find(s => s.cruise_name === activeTab)}
          onDownloadTicket={handleDownloadTicket}
          onSendWhatsApp={(b) => setSendTarget(b)}
          onRowSave={handleRowSave}
          onEditStateChange={setHasUnsavedRow}
          invoiceMap={invoiceMap}
          onGenerateInvoice={(b) => setInvoiceModal({ booking: b, existingInvoice: invoiceMap.get(b.id) })}
        />
      )}

    </div>
  );
}
